/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Api
 * JINGLE — Christmas scavenger hunt API
 * OpenAPI spec version: 0.1.0
 */
import * as zod from "zod";

/**
 * @summary Health check
 */
export const HealthCheckResponse = zod.object({
  status: zod.string(),
});

/**
 * Public endpoint — no auth required. Accepts a lead from the marketing
launch-updates form. Validates email format, rejects honeypot hits,
enforces per-IP rate limit, rejects duplicate emails (409), and stores
the lead in Postgres. Returns 201 on success.
Children's data must never be submitted — the form has no age field.

 * @summary Submit a launch-updates lead
 */
export const submitLaunchUpdateLeadBodyEmailMax = 254;

export const submitLaunchUpdateLeadBodyFirstNameMax = 80;

export const submitLaunchUpdateLeadBodyCountryMax = 80;

export const submitLaunchUpdateLeadBodyLanguageMax = 40;

export const submitLaunchUpdateLeadBodyInterestMax = 120;

export const submitLaunchUpdateLeadBodyLocaleMax = 20;

export const submitLaunchUpdateLeadBodyWebsiteMax = 0;

export const submitLaunchUpdateLeadBodyUtmSourceMax = 120;

export const submitLaunchUpdateLeadBodyUtmMediumMax = 120;

export const submitLaunchUpdateLeadBodyUtmCampaignMax = 120;

export const submitLaunchUpdateLeadBodyUtmContentMax = 120;

export const submitLaunchUpdateLeadBodyUtmTermMax = 120;

export const SubmitLaunchUpdateLeadBody = zod.object({
  email: zod.string().email().max(submitLaunchUpdateLeadBodyEmailMax),
  firstName: zod.string().max(submitLaunchUpdateLeadBodyFirstNameMax).nullish(),
  country: zod.string().max(submitLaunchUpdateLeadBodyCountryMax).nullish(),
  language: zod.string().max(submitLaunchUpdateLeadBodyLanguageMax).nullish(),
  familyRole: zod
    .enum(["parent", "grandparent", "aunt_uncle", "teacher", "other"])
    .nullish(),
  interest: zod.string().max(submitLaunchUpdateLeadBodyInterestMax).nullish(),
  locale: zod.string().max(submitLaunchUpdateLeadBodyLocaleMax).nullish(),
  consent: zod
    .boolean()
    .describe("Must be true. Submission is rejected if false."),
  website: zod
    .string()
    .max(submitLaunchUpdateLeadBodyWebsiteMax)
    .nullish()
    .describe("Honeypot — must be absent or empty string."),
  utmSource: zod.string().max(submitLaunchUpdateLeadBodyUtmSourceMax).nullish(),
  utmMedium: zod.string().max(submitLaunchUpdateLeadBodyUtmMediumMax).nullish(),
  utmCampaign: zod
    .string()
    .max(submitLaunchUpdateLeadBodyUtmCampaignMax)
    .nullish(),
  utmContent: zod
    .string()
    .max(submitLaunchUpdateLeadBodyUtmContentMax)
    .nullish(),
  utmTerm: zod.string().max(submitLaunchUpdateLeadBodyUtmTermMax).nullish(),
});

/**
 * Public endpoint — no auth required. Accepts a privacy
data-deletion or account-closure request from the legal pages.
Persists the request, fires an auto-reply email to the requester
with a case reference, and notifies the ops team. Rate limited
per IP.

 * @summary Submit a privacy data-deletion request
 */
export const submitDeletionRequestBodyEmailMax = 254;

export const submitDeletionRequestBodyDetailsMax = 2000;

export const submitDeletionRequestBodyLocaleMax = 20;

export const submitDeletionRequestBodyWebsiteMax = 0;

export const SubmitDeletionRequestBody = zod.object({
  email: zod.string().email().max(submitDeletionRequestBodyEmailMax),
  requestType: zod.enum(["data", "account"]),
  details: zod.string().max(submitDeletionRequestBodyDetailsMax).nullish(),
  locale: zod.string().max(submitDeletionRequestBodyLocaleMax).nullish(),
  website: zod
    .string()
    .max(submitDeletionRequestBodyWebsiteMax)
    .nullish()
    .describe("Honeypot — must be absent or empty."),
});

/**
 * Returns all current founder operators with display name, username, and
the date each was added. Also returns `currentUserId` so the client can
disable the self-removal button.
Non-founders receive 404 (same as all /api/founder/* routes) so the
route existence is not leaked.

 * @summary List all founder operators (founder-only, 404-cloaked)
 */
export const ListFounderOperatorsResponse = zod.object({
  operators: zod.array(
    zod.object({
      id: zod.number(),
      userId: zod.number(),
      username: zod.string(),
      displayName: zod.string(),
      notes: zod.string().nullish(),
      addedAt: zod.coerce.date(),
    }),
  ),
  currentUserId: zod
    .number()
    .nullable()
    .describe(
      "The userId of the currently authenticated founder (for self-removal guard).",
    ),
});

/**
 * Promotes an existing user to founder operator status. The user is
looked up by username (exact match) or email (case-insensitive).
Returns 400 if the identifier is missing, 404 if the user is not
found, 409 if they are already a founder operator.
Non-founders receive 403.
An admin_audit_events row (`founder_operator_added`) is written on
every successful add.

 * @summary Add a founder operator by username or email (founder-only)
 */
export const AddFounderOperatorBody = zod.object({
  username: zod
    .string()
    .describe(
      "Exact username or email address (case-insensitive for email) of the user to promote.",
    ),
});

export const AddFounderOperatorResponse = zod.object({
  ok: zod.boolean(),
  operator: zod.object({
    userId: zod.number(),
    username: zod.string(),
    displayName: zod.string(),
  }),
});

/**
 * Revokes founder operator status for the target user.
Returns 400 if `userId` is not a valid positive integer.
Returns 403 if the caller is not a founder, or if the target userId
equals the caller's own userId (self-removal guard).
Returns 404 if the target is not currently a founder operator.
An admin_audit_events row (`founder_operator_removed`) is written on
every successful removal.

 * @summary Remove a founder operator by userId (founder-only)
 */
export const RemoveFounderOperatorParams = zod.object({
  userId: zod.coerce
    .number()
    .describe("The numeric user ID of the operator to remove"),
});

export const RemoveFounderOperatorResponse = zod.object({
  ok: zod.boolean(),
});

/**
 * Returns the effective values used by the nightly founder alert scan
and email/Slack debounce, plus the persisted DB row, env override,
and default for each tunable so the UI can show provenance.
Non-founders receive 404.

 * @summary Read founder alert thresholds + debounce window (founder-only)
 */
export const GetFounderAlertConfigResponse = zod.object({
  effective: zod.object({
    stuckFamilyThreshold: zod.number(),
    serverError7dThreshold: zod.number(),
    voiceCapClusterThreshold: zod.number(),
    aiFailureThreshold: zod.number(),
    alertDebounceHours: zod
      .number()
      .describe("0 means 'use the per-severity default'."),
  }),
  items: zod.array(
    zod.object({
      key: zod.enum([
        "stuck_family_threshold",
        "server_error_7d_threshold",
        "voice_cap_cluster_threshold",
        "ai_failure_threshold",
        "alert_debounce_hours",
      ]),
      label: zod.string(),
      description: zod.string().optional(),
      effective: zod.number(),
      persisted: zod.number().nullable(),
      envOverride: zod.number().nullable(),
      default: zod.number(),
    }),
  ),
});

/**
 * Persist a new value for one of the allowlisted founder alert
tunables. Writes an `admin_audit_events` row
(`founder_alert_config_updated`) on every successful update.
Returns the new effective config.

 * @summary Update one founder alert tunable (founder-only)
 */
export const updateFounderAlertConfigBodyValueMin = 0;
export const updateFounderAlertConfigBodyValueMax = 100000;

export const UpdateFounderAlertConfigBody = zod.object({
  key: zod.enum([
    "stuck_family_threshold",
    "server_error_7d_threshold",
    "voice_cap_cluster_threshold",
    "ai_failure_threshold",
    "alert_debounce_hours",
  ]),
  value: zod
    .number()
    .min(updateFounderAlertConfigBodyValueMin)
    .max(updateFounderAlertConfigBodyValueMax),
});

export const UpdateFounderAlertConfigResponse = zod.object({
  effective: zod.object({
    stuckFamilyThreshold: zod.number(),
    serverError7dThreshold: zod.number(),
    voiceCapClusterThreshold: zod.number(),
    aiFailureThreshold: zod.number(),
    alertDebounceHours: zod
      .number()
      .describe("0 means 'use the per-severity default'."),
  }),
  items: zod.array(
    zod.object({
      key: zod.enum([
        "stuck_family_threshold",
        "server_error_7d_threshold",
        "voice_cap_cluster_threshold",
        "ai_failure_threshold",
        "alert_debounce_hours",
      ]),
      label: zod.string(),
      description: zod.string().optional(),
      effective: zod.number(),
      persisted: zod.number().nullable(),
      envOverride: zod.number().nullable(),
      default: zod.number(),
    }),
  ),
});

/**
 * @summary Request a presigned URL for file upload
 */

export const RequestUploadUrlBody = zod.object({
  name: zod.string().min(1),
  size: zod.number().min(1),
  contentType: zod.string().min(1),
});

export const RequestUploadUrlResponse = zod.object({
  uploadURL: zod.string().url(),
  objectPath: zod.string(),
  metadata: zod
    .object({
      name: zod.string().min(1),
      size: zod.number().min(1),
      contentType: zod.string().min(1),
    })
    .optional(),
});

/**
 * @summary Serve a public asset
 */
export const GetPublicObjectParams = zod.object({
  filePath: zod.coerce.string(),
});

/**
 * @summary Serve an object entity
 */
export const GetStorageObjectParams = zod.object({
  objectPath: zod.coerce.string(),
});

/**
 * @summary Join a team using an invite code (legacy)
 */

export const JoinSessionBody = zod.object({
  inviteCode: zod.string().min(1),
  playerName: zod.string().optional(),
});

export const JoinSessionResponse = zod.object({
  authenticated: zod.boolean(),
  userId: zod
    .number()
    .optional()
    .describe("Authenticated user's id. Present iff authenticated=true."),
  team: zod
    .object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
      colorHex: zod.string(),
      totem: zod.string(),
      motto: zod.string().optional(),
      score: zod.number().optional(),
    })
    .optional(),
  playerName: zod.string().optional(),
  username: zod.string().optional(),
  isAdmin: zod.boolean().optional(),
  mustResetPassword: zod.boolean().optional(),
  needsOnboarding: zod
    .boolean()
    .optional()
    .describe(
      "True when the user has not yet completed the cinematic onboarding intro. False once POST \/session\/onboarding-complete has been called.",
    ),
  tenant: zod
    .object({
      id: zod.number(),
      name: zod.string(),
      slug: zod.string(),
    })
    .optional(),
  role: zod.string().optional().describe("owner | admin | player | child"),
});

/**
 * @summary Log in with username and password
 */

export const LoginSessionBody = zod.object({
  username: zod.string().min(1),
  password: zod.string().min(1),
});

export const LoginSessionResponse = zod.object({
  authenticated: zod.boolean(),
  userId: zod
    .number()
    .optional()
    .describe("Authenticated user's id. Present iff authenticated=true."),
  team: zod
    .object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
      colorHex: zod.string(),
      totem: zod.string(),
      motto: zod.string().optional(),
      score: zod.number().optional(),
    })
    .optional(),
  playerName: zod.string().optional(),
  username: zod.string().optional(),
  isAdmin: zod.boolean().optional(),
  mustResetPassword: zod.boolean().optional(),
  needsOnboarding: zod
    .boolean()
    .optional()
    .describe(
      "True when the user has not yet completed the cinematic onboarding intro. False once POST \/session\/onboarding-complete has been called.",
    ),
  tenant: zod
    .object({
      id: zod.number(),
      name: zod.string(),
      slug: zod.string(),
    })
    .optional(),
  role: zod.string().optional().describe("owner | admin | player | child"),
});

/**
 * @summary Change the current user's password (also clears the must-reset flag)
 */
export const changePasswordBodyNewPasswordMin = 6;
export const changePasswordBodyNewPasswordMax = 200;

export const ChangePasswordBody = zod.object({
  newPassword: zod
    .string()
    .min(changePasswordBodyNewPasswordMin)
    .max(changePasswordBodyNewPasswordMax),
});

export const ChangePasswordResponse = zod.object({
  authenticated: zod.boolean(),
  userId: zod
    .number()
    .optional()
    .describe("Authenticated user's id. Present iff authenticated=true."),
  team: zod
    .object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
      colorHex: zod.string(),
      totem: zod.string(),
      motto: zod.string().optional(),
      score: zod.number().optional(),
    })
    .optional(),
  playerName: zod.string().optional(),
  username: zod.string().optional(),
  isAdmin: zod.boolean().optional(),
  mustResetPassword: zod.boolean().optional(),
  needsOnboarding: zod
    .boolean()
    .optional()
    .describe(
      "True when the user has not yet completed the cinematic onboarding intro. False once POST \/session\/onboarding-complete has been called.",
    ),
  tenant: zod
    .object({
      id: zod.number(),
      name: zod.string(),
      slug: zod.string(),
    })
    .optional(),
  role: zod.string().optional().describe("owner | admin | player | child"),
});

/**
 * Public endpoint. Always returns `{ ok: true }` regardless of whether
the identifier matches a known user — this prevents account
enumeration. The response is intentionally generic and the real work
(DB lookup + email send) happens asynchronously after the response is
sent so timing is uniform across all branches.

 * @summary Request a password reset code via email
 */
export const requestPasswordResetBodyIdentifierMax = 200;

export const RequestPasswordResetBody = zod.object({
  identifier: zod
    .string()
    .min(1)
    .max(requestPasswordResetBodyIdentifierMax)
    .describe("Email address or username for the account being reset."),
});

export const RequestPasswordResetResponse = zod.object({
  ok: zod.boolean(),
});

/**
 * Public endpoint. Validates the one-time code and rotates the user's
password. On success the server drops a fresh session cookie. All
failure modes (bad code, expired, identifier mismatch) surface a
generic 400 to avoid enumeration.

 * @summary Redeem a password reset code and set a new password
 */
export const verifyPasswordResetBodyIdentifierMax = 200;

export const verifyPasswordResetBodyCodeRegExp = new RegExp("^\\d{4,8}$");
export const verifyPasswordResetBodyNewPasswordMin = 6;
export const verifyPasswordResetBodyNewPasswordMax = 200;

export const VerifyPasswordResetBody = zod.object({
  identifier: zod.string().min(1).max(verifyPasswordResetBodyIdentifierMax),
  code: zod
    .string()
    .regex(verifyPasswordResetBodyCodeRegExp)
    .describe("The one-time numeric reset code emailed to the user."),
  newPassword: zod
    .string()
    .min(verifyPasswordResetBodyNewPasswordMin)
    .max(verifyPasswordResetBodyNewPasswordMax)
    .describe("New password — same min\/max as the signup contract."),
});

export const VerifyPasswordResetResponse = zod.object({
  ok: zod.boolean(),
  signedIn: zod.boolean().optional(),
  authenticated: zod.boolean().optional(),
  team: zod
    .object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
      colorHex: zod.string(),
      totem: zod.string(),
      motto: zod.string().optional(),
      score: zod.number().optional(),
    })
    .optional(),
  playerName: zod.string().optional(),
  username: zod.string().optional(),
  isAdmin: zod.boolean().optional(),
  mustResetPassword: zod.boolean().optional(),
  tenant: zod
    .object({
      id: zod.number(),
      name: zod.string(),
      slug: zod.string(),
    })
    .optional(),
  role: zod.string().optional(),
});

/**
 * @summary Get current session and team
 */
export const GetSessionResponse = zod.object({
  authenticated: zod.boolean(),
  userId: zod
    .number()
    .optional()
    .describe("Authenticated user's id. Present iff authenticated=true."),
  team: zod
    .object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
      colorHex: zod.string(),
      totem: zod.string(),
      motto: zod.string().optional(),
      score: zod.number().optional(),
    })
    .optional(),
  playerName: zod.string().optional(),
  username: zod.string().optional(),
  isAdmin: zod.boolean().optional(),
  mustResetPassword: zod.boolean().optional(),
  needsOnboarding: zod
    .boolean()
    .optional()
    .describe(
      "True when the user has not yet completed the cinematic onboarding intro. False once POST \/session\/onboarding-complete has been called.",
    ),
  tenant: zod
    .object({
      id: zod.number(),
      name: zod.string(),
      slug: zod.string(),
    })
    .optional(),
  role: zod.string().optional().describe("owner | admin | player | child"),
});

/**
 * @summary List all teams
 */
export const ListTeamsResponseItem = zod.object({
  id: zod.number(),
  slug: zod.string(),
  name: zod.string(),
  colorHex: zod.string(),
  totem: zod.string(),
  motto: zod.string().optional(),
  score: zod.number().optional(),
});
export const ListTeamsResponse = zod.array(ListTeamsResponseItem);

/**
 * @summary Get current team detail
 */
export const GetMyTeamResponse = zod
  .object({
    id: zod.number(),
    slug: zod.string(),
    name: zod.string(),
    colorHex: zod.string(),
    totem: zod.string(),
    motto: zod.string().optional(),
    score: zod.number().optional(),
  })
  .and(
    zod.object({
      unreadCount: zod.number().optional(),
      findsCount: zod.number().optional(),
      activeHunts: zod.number().optional(),
      elfSlug: zod.string().nullish(),
    }),
  );

/**
 * Returns the caller's deterministic elf assignment for the active
tenant. If the caller does not yet have an assignment, one is
created on the fly using the deterministic algorithm described in
`ELF_LIBRARY_AND_ASSIGNMENT_REPORT.md`. Tenant-scoped: the active
tenant is resolved server-side from the session — never from the
client.

 * @summary Read (and lazily create) the caller's elf assignment in the active tenant
 */
export const GetMyElfAssignmentResponse = zod.object({
  membershipId: zod.number(),
  userId: zod.number().nullable(),
  elfId: zod.string(),
  assignedAt: zod.coerce.date(),
  assignmentSource: zod.enum(["auto", "backfill", "manual"]),
  elf: zod.object({
    id: zod.string(),
    name: zod.string(),
    title: zod.string(),
    specialty: zod.string(),
    personality: zod.string(),
    catchphrase: zod.string(),
    colorTheme: zod.object({
      primary: zod.string(),
      secondary: zod.string(),
    }),
    portraitPath: zod.string(),
    altText: zod.string(),
  }),
});

/**
 * Returns the active tenant's full roster with each active member's
elf assignment (or null when none exists yet — this endpoint is
read-only and never auto-creates assignments). Owner/admin
membership role required; players receive 403.

 * @summary Host-only roster of every active member + their elf
 */
export const GetFamilyElfRosterResponse = zod.object({
  roster: zod.array(
    zod.object({
      membershipId: zod.number(),
      userId: zod.number().nullable(),
      role: zod.string(),
      displayName: zod.string(),
      assignment: zod.union([
        zod.object({
          membershipId: zod.number(),
          userId: zod.number().nullable(),
          elfId: zod.string(),
          assignedAt: zod.coerce.date(),
          assignmentSource: zod.enum(["auto", "backfill", "manual"]),
          elf: zod.object({
            id: zod.string(),
            name: zod.string(),
            title: zod.string(),
            specialty: zod.string(),
            personality: zod.string(),
            catchphrase: zod.string(),
            colorTheme: zod.object({
              primary: zod.string(),
              secondary: zod.string(),
            }),
            portraitPath: zod.string(),
            altText: zod.string(),
          }),
        }),
        zod.null(),
      ]),
    }),
  ),
  librarySize: zod.number(),
});

/**
 * @summary List all elves
 */
export const ListElvesResponseItem = zod.object({
  slug: zod.string(),
  name: zod.string(),
  role: zod.string(),
  personality: zod.string(),
  voice: zod.string(),
  portraitUrl: zod.string(),
  signature: zod.string().optional(),
  accentColor: zod.string().optional(),
});
export const ListElvesResponse = zod.array(ListElvesResponseItem);

/**
 * @summary Get elf detail
 */
export const GetElfParams = zod.object({
  slug: zod.coerce.string(),
});

export const GetElfResponse = zod.object({
  slug: zod.string(),
  name: zod.string(),
  role: zod.string(),
  personality: zod.string(),
  voice: zod.string(),
  portraitUrl: zod.string(),
  signature: zod.string().optional(),
  accentColor: zod.string().optional(),
});

/**
 * @summary Generate TTS audio for an elf line
 */
export const SpeakElfParams = zod.object({
  slug: zod.coerce.string(),
});

export const speakElfBodyTextMax = 1200;

export const SpeakElfBody = zod.object({
  text: zod.string().min(1).max(speakElfBodyTextMax),
});

/**
 * Returns the most recent inbox messages, newest first. Optional
`?limit=` (default 50, max 100) caps the page size. Optional
`?cursor=ID` returns the next page of messages older than the
given row id — clients pass the id of the last message they
already have (or the value of the `X-Next-Cursor` response
header from the previous request). Both params are additive:
omit them and the response is the same first-page array
existing callers expect.

 * @summary List elf messages for the current team
 */
export const listInboxQueryLimitMax = 100;

export const ListInboxQueryParams = zod.object({
  limit: zod.coerce.number().min(1).max(listInboxQueryLimitMax).optional(),
  cursor: zod.coerce.number().min(1).optional(),
});

export const ListInboxResponseItem = zod.object({
  id: zod.number(),
  elfSlug: zod.string(),
  elfName: zod.string(),
  kind: zod
    .string()
    .describe("narrative | hint | praise | warning | postcard | sleigh"),
  title: zod.string().optional(),
  body: zod.string(),
  imageUrl: zod.string().optional(),
  huntId: zod.number().optional(),
  createdAt: zod.coerce.date(),
  isRead: zod.boolean(),
});
export const ListInboxResponse = zod.array(ListInboxResponseItem);

/**
 * @summary Mark a message as read
 */
export const MarkInboxReadParams = zod.object({
  id: zod.coerce.number(),
});

export const MarkInboxReadResponse = zod.object({
  id: zod.number(),
  elfSlug: zod.string(),
  elfName: zod.string(),
  kind: zod
    .string()
    .describe("narrative | hint | praise | warning | postcard | sleigh"),
  title: zod.string().optional(),
  body: zod.string(),
  imageUrl: zod.string().optional(),
  huntId: zod.number().optional(),
  createdAt: zod.coerce.date(),
  isRead: zod.boolean(),
});

/**
 * Active mission clues for the current team, ordered by `(day, id)`
ascending. Optional `?limit=` (default 50, max 100) caps the page
size. Optional `?cursor=ID` returns the slice that comes AFTER
the cursor row in `(day, id)` order — clients pass the id of the
last hunt they already have (or the value of the `X-Next-Cursor`
response header from the previous request). The server resolves
the cursor's `day` and applies the compound predicate
`day > cursor.day OR (day = cursor.day AND id > cursor.id)`,
so pagination stays correct even when monotonic id and day
disagree (e.g., admin backfills, mission-generator inserts).
Both params are additive and preserve the legacy array
response shape.

 * @summary Active hunts for the current team
 */
export const listActiveHuntsQueryLimitMax = 100;

export const ListActiveHuntsQueryParams = zod.object({
  limit: zod.coerce
    .number()
    .min(1)
    .max(listActiveHuntsQueryLimitMax)
    .optional(),
  cursor: zod.coerce.number().min(1).optional(),
});

export const ListActiveHuntsResponseItem = zod.object({
  id: zod.number(),
  title: zod.string(),
  clue: zod.string(),
  status: zod.string().describe("open | found | expired"),
  difficulty: zod.string(),
  points: zod.number(),
  elfSlug: zod.string().optional(),
  roomHint: zod.string().optional(),
  targetDescription: zod.string().optional(),
  missionMomentKind: zod
    .enum([
      "photo_find",
      "santa_signal",
      "elf_evidence",
      "coalie_mischief",
      "kindness_mission",
      "family_pose",
      "cozy_glow",
      "memory_moment",
      "reindeer_fuel",
      "riddle_answer",
    ])
    .optional(),
});
export const ListActiveHuntsResponse = zod.array(ListActiveHuntsResponseItem);

/**
 * @summary Hunt detail (with previous attempts)
 */
export const GetHuntParams = zod.object({
  id: zod.coerce.number(),
});

export const GetHuntResponse = zod
  .object({
    id: zod.number(),
    title: zod.string(),
    clue: zod.string(),
    status: zod.string().describe("open | found | expired"),
    difficulty: zod.string(),
    points: zod.number(),
    elfSlug: zod.string().optional(),
    roomHint: zod.string().optional(),
    targetDescription: zod.string().optional(),
    missionMomentKind: zod
      .enum([
        "photo_find",
        "santa_signal",
        "elf_evidence",
        "coalie_mischief",
        "kindness_mission",
        "family_pose",
        "cozy_glow",
        "memory_moment",
        "reindeer_fuel",
        "riddle_answer",
      ])
      .optional(),
  })
  .and(
    zod.object({
      attempts: zod
        .array(
          zod.object({
            id: zod.number(),
            huntId: zod.number(),
            teamId: zod.number(),
            teamName: zod.string().optional(),
            teamColor: zod.string().optional(),
            huntTitle: zod.string().optional(),
            imageUrl: zod.string(),
            verified: zod.boolean(),
            craftScore: zod.number().optional(),
            feedback: zod.string().optional(),
            roomName: zod.string().optional(),
            lat: zod.number().optional(),
            lng: zod.number().optional(),
            createdAt: zod.coerce.date(),
          }),
        )
        .optional(),
    }),
  );

/**
 * @summary Submit a photo for a hunt; verifies with Claude Vision
 */
export const SubmitFindParams = zod.object({
  id: zod.coerce.number(),
});

export const submitFindBodyClientSubmissionIdMin = 8;
export const submitFindBodyClientSubmissionIdMax = 128;

export const submitFindBodyClientSubmissionIdRegExp = new RegExp(
  "^[A-Za-z0-9_-]+$",
);

export const SubmitFindBody = zod.object({
  objectPath: zod.string(),
  lat: zod.number().optional(),
  lng: zod.number().optional(),
  roomName: zod.string().optional(),
  notes: zod.string().optional(),
  clientSubmissionId: zod
    .string()
    .min(submitFindBodyClientSubmissionIdMin)
    .max(submitFindBodyClientSubmissionIdMax)
    .regex(submitFindBodyClientSubmissionIdRegExp)
    .optional(),
});

export const SubmitFindResponse = zod.object({
  verified: zod.boolean(),
  craftScore: zod.number().nullish().describe("0-100"),
  feedback: zod.string().nullish(),
  elfReaction: zod.string().nullish(),
  elfSlug: zod.string().optional(),
  pointsAwarded: zod.number().optional(),
  deduped: zod.boolean().optional(),
  deleted: zod.boolean().optional(),
  find: zod
    .object({
      id: zod.number(),
      huntId: zod.number(),
      teamId: zod.number(),
      teamName: zod.string().optional(),
      teamColor: zod.string().optional(),
      huntTitle: zod.string().optional(),
      imageUrl: zod.string(),
      verified: zod.boolean(),
      craftScore: zod.number().optional(),
      feedback: zod.string().optional(),
      roomName: zod.string().optional(),
      lat: zod.number().optional(),
      lng: zod.number().optional(),
      createdAt: zod.coerce.date(),
    })
    .nullish(),
});

/**
 * Most recent finds for the current team, newest first. Optional
`?limit=` (default 50, max 100). Optional `?cursor=ID` returns
finds older than the given row id, so clients can lazily load
deeper history without a single huge response. Pass the value
of the `X-Next-Cursor` response header from the previous
request to fetch the next page. Both params are additive and
preserve the legacy array response shape.

 * @summary All finds by current team
 */
export const listMyFindsQueryLimitMax = 100;

export const ListMyFindsQueryParams = zod.object({
  limit: zod.coerce.number().min(1).max(listMyFindsQueryLimitMax).optional(),
  cursor: zod.coerce.number().min(1).optional(),
});

export const ListMyFindsResponseItem = zod.object({
  id: zod.number(),
  huntId: zod.number(),
  teamId: zod.number(),
  teamName: zod.string().optional(),
  teamColor: zod.string().optional(),
  huntTitle: zod.string().optional(),
  imageUrl: zod.string(),
  verified: zod.boolean(),
  craftScore: zod.number().optional(),
  feedback: zod.string().optional(),
  roomName: zod.string().optional(),
  lat: zod.number().optional(),
  lng: zod.number().optional(),
  createdAt: zod.coerce.date(),
});
export const ListMyFindsResponse = zod.array(ListMyFindsResponseItem);

/**
 * All verified finds across the active mission, newest first.
Optional `?limit=` (default 50, max 100). Optional `?cursor=ID`
returns pins older than the given row id — clients pass the id
of the last pin already on the map (or the value of the
`X-Next-Cursor` response header from the previous request) to
load older ones. Both params are additive and preserve the
legacy array response shape. The aurora heatmap endpoint is
intentionally not cursor-paginated (it returns the full
aggregate snapshot).

 * @summary All finds across all teams (for the living map)
 */
export const listMapFindsQueryLimitMax = 100;

export const ListMapFindsQueryParams = zod.object({
  limit: zod.coerce.number().min(1).max(listMapFindsQueryLimitMax).optional(),
  cursor: zod.coerce.number().min(1).optional(),
});

export const ListMapFindsResponseItem = zod.object({
  id: zod.number(),
  teamId: zod.number(),
  teamName: zod.string().optional(),
  teamColor: zod.string(),
  huntTitle: zod.string().optional(),
  imageUrl: zod.string().optional(),
  lat: zod.number(),
  lng: zod.number(),
  verified: zod.boolean(),
  craftScore: zod.number().optional(),
  createdAt: zod.coerce.date().optional(),
});
export const ListMapFindsResponse = zod.array(ListMapFindsResponseItem);

/**
 * @summary Aurora intensity grid (per team color heatmap)
 */
export const GetAuroraResponse = zod.object({
  coalIntensity: zod.number().optional(),
  cells: zod.array(
    zod.object({
      lat: zod.number(),
      lng: zod.number(),
      intensity: zod.number(),
      teamColor: zod.string(),
      teamId: zod.number().optional(),
    }),
  ),
});

/**
 * Daily postcards for the current team, newest first. Optional
`?limit=` (default 50, max 100). Optional `?cursor=ID` returns
postcards older than the given row id so families with months
of past Christmas missions can fetch deeper history on demand.
Pass the value of the `X-Next-Cursor` response header from the
previous request to fetch the next page. Both params are
additive and preserve the legacy array response shape.

 * @summary Daily postcards for current team
 */
export const listPostcardsQueryLimitMax = 100;

export const ListPostcardsQueryParams = zod.object({
  limit: zod.coerce.number().min(1).max(listPostcardsQueryLimitMax).optional(),
  cursor: zod.coerce.number().min(1).optional(),
});

export const ListPostcardsResponseItem = zod.object({
  id: zod.number(),
  day: zod.number(),
  title: zod.string(),
  body: zod.string(),
  imageUrl: zod.string(),
  elfSlug: zod.string().optional(),
  createdAt: zod.coerce.date(),
});
export const ListPostcardsResponse = zod.array(ListPostcardsResponseItem);

/**
 * @summary Global game state — day, moment status, leaderboard
 */
export const GetGameStateResponse = zod.object({
  day: zod.number(),
  totalDays: zod.number(),
  currentDate: zod.string().optional(),
  currentMoment: zod.object({
    type: zod
      .string()
      .describe("sleigh | aurora-storm | coalie | family | none"),
    active: zod.boolean(),
    startedAt: zod.coerce.date(),
    title: zod.string().optional(),
    body: zod.string().optional(),
    imageUrl: zod.string().optional(),
    durationSeconds: zod.number().optional(),
  }),
  sleighEtaSeconds: zod.number(),
  coalThreatLevel: zod.number().optional(),
  leaderboard: zod.array(
    zod.object({
      teamId: zod.number(),
      name: zod.string(),
      colorHex: zod.string(),
      score: zod.number(),
      findsCount: zod.number().optional(),
    }),
  ),
});

/**
 * @summary Active mission's Santa-intro narrative beat (player-facing)
 */
export const GetMissionNarrativeResponse = zod
  .object({
    missionId: zod.number(),
    text: zod.string().nullable(),
    locale: zod.string(),
    characterSlug: zod
      .enum(["santa", "brian"])
      .describe(
        "Which character voices a given mission narrative beat. Santa\nnarrates the canonical intro by default; Brian may co-narrate\nwarmer moments such as some intro beats and final-reveal\ncelebrations. Coalie is intentionally excluded — Coalie is\nscripted as the interruption, not as a narrator.\n",
      ),
    available: zod.boolean(),
  })
  .describe(
    "Per-mission Santa-intro narrative beat. `text` is null when the\nhost hasn't authored the script or the mission isn't active yet;\nthe UI uses `available` to decide whether to mount the surface.\n",
  );

/**
 * @summary Admin dashboard state
 */
export const GetAdminStateResponse = zod.object({
  teams: zod.array(
    zod.object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
      colorHex: zod.string(),
      totem: zod.string(),
      motto: zod.string().optional(),
      score: zod.number().optional(),
    }),
  ),
  elves: zod.array(
    zod.object({
      slug: zod.string(),
      name: zod.string(),
      role: zod.string(),
      personality: zod.string(),
      voice: zod.string(),
      portraitUrl: zod.string(),
      signature: zod.string().optional(),
      accentColor: zod.string().optional(),
    }),
  ),
  hunts: zod
    .array(
      zod.object({
        id: zod.number(),
        title: zod.string(),
        clue: zod.string(),
        status: zod.string().describe("open | found | expired"),
        difficulty: zod.string(),
        points: zod.number(),
        elfSlug: zod.string().optional(),
        roomHint: zod.string().optional(),
        targetDescription: zod.string().optional(),
        missionMomentKind: zod
          .enum([
            "photo_find",
            "santa_signal",
            "elf_evidence",
            "coalie_mischief",
            "kindness_mission",
            "family_pose",
            "cozy_glow",
            "memory_moment",
            "reindeer_fuel",
            "riddle_answer",
          ])
          .optional(),
      }),
    )
    .optional(),
  recentFinds: zod.array(
    zod.object({
      id: zod.number(),
      huntId: zod.number(),
      teamId: zod.number(),
      teamName: zod.string().optional(),
      teamColor: zod.string().optional(),
      huntTitle: zod.string().optional(),
      imageUrl: zod.string(),
      verified: zod.boolean(),
      craftScore: zod.number().optional(),
      feedback: zod.string().optional(),
      roomName: zod.string().optional(),
      lat: zod.number().optional(),
      lng: zod.number().optional(),
      createdAt: zod.coerce.date(),
    }),
  ),
  recentMessages: zod.array(
    zod.object({
      id: zod.number(),
      elfSlug: zod.string(),
      elfName: zod.string(),
      kind: zod
        .string()
        .describe("narrative | hint | praise | warning | postcard | sleigh"),
      title: zod.string().optional(),
      body: zod.string(),
      imageUrl: zod.string().optional(),
      huntId: zod.number().optional(),
      createdAt: zod.coerce.date(),
      isRead: zod.boolean(),
    }),
  ),
  gameState: zod.object({
    day: zod.number(),
    totalDays: zod.number(),
    currentDate: zod.string().optional(),
    currentMoment: zod.object({
      type: zod
        .string()
        .describe("sleigh | aurora-storm | coalie | family | none"),
      active: zod.boolean(),
      startedAt: zod.coerce.date(),
      title: zod.string().optional(),
      body: zod.string().optional(),
      imageUrl: zod.string().optional(),
      durationSeconds: zod.number().optional(),
    }),
    sleighEtaSeconds: zod.number(),
    coalThreatLevel: zod.number().optional(),
    leaderboard: zod.array(
      zod.object({
        teamId: zod.number(),
        name: zod.string(),
        colorHex: zod.string(),
        score: zod.number(),
        findsCount: zod.number().optional(),
      }),
    ),
  }),
  mission: zod
    .object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
      status: zod.string(),
      locationKind: zod.string(),
      startsAt: zod.coerce.date().nullish(),
      sleighArrivalAt: zod.coerce.date().nullish(),
      totalDays: zod.number(),
    })
    .nullish(),
});

/**
 * @summary Fire a synchronized moment (e.g. sleigh arrival)
 */
export const TriggerMomentBody = zod.object({
  type: zod.string(),
  durationSeconds: zod.number().optional(),
  customTitle: zod.string().optional(),
  customBody: zod.string().optional(),
});

export const TriggerMomentResponse = zod.object({
  type: zod.string().describe("sleigh | aurora-storm | coalie | family | none"),
  active: zod.boolean(),
  startedAt: zod.coerce.date(),
  title: zod.string().optional(),
  body: zod.string().optional(),
  imageUrl: zod.string().optional(),
  durationSeconds: zod.number().optional(),
});

/**
 * @summary Generate and send an elf message to one or all teams
 */
export const SeedElfMessageBody = zod.object({
  elfSlug: zod.string(),
  teamId: zod.number().optional().describe("If null, sends to all teams"),
  kind: zod.string(),
  promptHint: zod.string().optional(),
  title: zod.string().optional(),
});

export const SeedElfMessageResponseItem = zod.object({
  id: zod.number(),
  elfSlug: zod.string(),
  elfName: zod.string(),
  kind: zod
    .string()
    .describe("narrative | hint | praise | warning | postcard | sleigh"),
  title: zod.string().optional(),
  body: zod.string(),
  imageUrl: zod.string().optional(),
  huntId: zod.number().optional(),
  createdAt: zod.coerce.date(),
  isRead: zod.boolean(),
});
export const SeedElfMessageResponse = zod.array(SeedElfMessageResponseItem);

/**
 * @summary Advance the simulated game day
 */
export const AdvanceDayBody = zod.object({
  day: zod
    .number()
    .optional()
    .describe("If provided, set absolute day; otherwise +1"),
});

export const AdvanceDayResponse = zod.object({
  day: zod.number(),
  totalDays: zod.number(),
  currentDate: zod.string().optional(),
  currentMoment: zod.object({
    type: zod
      .string()
      .describe("sleigh | aurora-storm | coalie | family | none"),
    active: zod.boolean(),
    startedAt: zod.coerce.date(),
    title: zod.string().optional(),
    body: zod.string().optional(),
    imageUrl: zod.string().optional(),
    durationSeconds: zod.number().optional(),
  }),
  sleighEtaSeconds: zod.number(),
  coalThreatLevel: zod.number().optional(),
  leaderboard: zod.array(
    zod.object({
      teamId: zod.number(),
      name: zod.string(),
      colorHex: zod.string(),
      score: zod.number(),
      findsCount: zod.number().optional(),
    }),
  ),
});

/**
 * @summary Read-only family setup card for the host
 */
export const GetAdminFamilyResponse = zod.object({
  id: zod.number(),
  name: zod.string(),
  slug: zod.string(),
  role: zod.string().describe("owner | admin | player | child"),
  memberCount: zod.number(),
  teamCount: zod.number(),
});

/**
 * @summary List active memberships in the current family
 */
export const ListAdminMembersResponseItem = zod.object({
  membershipId: zod.number(),
  userId: zod.number(),
  username: zod.string(),
  displayName: zod.string(),
  role: zod.string(),
  joinedAt: zod.coerce.date(),
  teamId: zod.number().nullish(),
  teamName: zod.string().nullish(),
  teamColorHex: zod.string().nullish(),
  mustResetPassword: zod.boolean(),
  isYou: zod.boolean(),
});
export const ListAdminMembersResponse = zod.array(ListAdminMembersResponseItem);

/**
 * @summary Revoke a member from the current family
 */
export const RemoveAdminMemberParams = zod.object({
  membershipId: zod.coerce.number(),
});

export const removeAdminMemberBodyReasonMax = 280;

export const RemoveAdminMemberBody = zod.object({
  reason: zod.string().max(removeAdminMemberBodyReasonMax).optional(),
});

export const RemoveAdminMemberResponse = zod.object({
  ok: zod.boolean(),
  membershipId: zod.number(),
});

/**
 * Family Safety + Kid Mode v1, plus co-host promote (Task #322). The host can flip a member between `child` and `player`, and the family owner can promote a `player` to `admin` (co-host) or demote an `admin` back to `player`. The family `owner` role can never be changed through this endpoint. A `child` profile cannot be jumped directly to `admin`: the host must mark them as `player` first.

 * @summary Change a member's role between child, player, and admin (co-host)
 */
export const SetAdminMemberRoleParams = zod.object({
  membershipId: zod.coerce.number(),
});

export const SetAdminMemberRoleBody = zod.object({
  role: zod
    .enum(["child", "player", "admin"])
    .describe(
      "Target role. `child` locks the member out of host\/admin\/billing surfaces. `player` is the standard grown-up player role. `admin` promotes a player to co-host (only the family owner can grant this).\n",
    ),
});

export const SetAdminMemberRoleResponse = zod.object({
  ok: zod.boolean(),
  membershipId: zod.number(),
});

/**
 * @summary Issue a one-time temporary password and force change on next login
 */
export const ResetAdminMemberPasswordParams = zod.object({
  membershipId: zod.coerce.number(),
});

export const ResetAdminMemberPasswordResponse = zod.object({
  ok: zod.boolean(),
  user: zod.object({
    id: zod.number(),
    username: zod.string(),
    displayName: zod.string(),
  }),
  tempPassword: zod.string(),
  mustResetPassword: zod.boolean(),
});

/**
 * @summary List every invitation for the current family
 */
export const ListAdminInvitationsResponseItem = zod.object({
  id: zod.number(),
  code: zod.string(),
  role: zod.string(),
  status: zod.string().describe("active | expired | used | revoked"),
  createdAt: zod.coerce.date(),
  expiresAt: zod.coerce.date().nullish(),
  revokedAt: zod.coerce.date().nullish(),
  useCount: zod.number(),
  maxUses: zod.number().nullish(),
  createdByDisplayName: zod.string().nullish(),
});
export const ListAdminInvitationsResponse = zod.array(
  ListAdminInvitationsResponseItem,
);

/**
 * @summary Mint a fresh invitation code
 */
export const mintAdminInvitationBodyExpiresInHoursMax = 336;

export const mintAdminInvitationBodyMaxUsesMax = 100;

export const MintAdminInvitationBody = zod.object({
  expiresInHours: zod
    .number()
    .min(1)
    .max(mintAdminInvitationBodyExpiresInHoursMax)
    .optional(),
  maxUses: zod
    .number()
    .min(1)
    .max(mintAdminInvitationBodyMaxUsesMax)
    .optional(),
  role: zod.enum(["player", "admin", "child"]).optional(),
});

export const MintAdminInvitationResponse = zod.object({
  invitation: zod.object({
    id: zod.number(),
    code: zod.string(),
    role: zod.string(),
    expiresAt: zod.coerce.date().nullish(),
    maxUses: zod.number().nullish(),
    useCount: zod.number(),
  }),
});

/**
 * @summary Revoke an invitation
 */
export const RevokeAdminInvitationParams = zod.object({
  id: zod.coerce.number(),
});

export const RevokeAdminInvitationResponse = zod.object({
  ok: zod.boolean(),
  id: zod.number(),
  code: zod.string(),
});

/**
 * @summary Derived "First Mission Launchpad" readiness for the host's active mission
 */
export const GetAdminLaunchpadResponse = zod.object({
  family: zod.object({
    name: zod.string(),
    slug: zod.string(),
  }),
  mission: zod
    .object({
      name: zod.string(),
      status: zod.string().describe("draft | active | completed | archived"),
    })
    .nullish(),
  summary: zod.object({
    approvedClues: zod.number(),
    draftClues: zod.number(),
    members: zod
      .number()
      .describe("All non-revoked memberships, including the host."),
    players: zod
      .number()
      .describe(
        "Non-host memberships only — proxies whether anyone has joined.",
      ),
    finds: zod.number().describe("Non-soft-deleted finds in this mission."),
    publishedRecap: zod.boolean(),
    draftRecap: zod.boolean(),
    activeInvitations: zod.number(),
  }),
  items: zod.array(
    zod.object({
      key: zod
        .string()
        .describe(
          "Canonical readiness key — the client maps this to launchpad.items.<key>.{title,description,cta} i18n keys.",
        ),
      status: zod
        .string()
        .describe("done | needs_action | waiting | optional | blocked"),
      ctaHref: zod
        .string()
        .nullish()
        .describe(
          "Pre-resolved href the host can navigate to for this item (anchor link or relative path). Null when no action makes sense.",
        ),
      owner: zod
        .string()
        .describe(
          "host | founder | player — who is meant to act on this item. v1 returns 'host' for everything.",
        ),
    }),
  ),
  nextRecommendedAction: zod
    .object({
      key: zod
        .string()
        .describe("Same canonical key vocabulary as AdminLaunchpadItem.key."),
      href: zod.string(),
    })
    .nullish(),
  overall: zod.string().describe("ready | almost | needs_setup | watch"),
  feedback: zod.object({
    url: zod.string().nullable(),
    email: zod.string().nullable(),
  }),
});

/**
 * @summary Soft-delete a find photo (and undo its score, if any)
 */
export const DeleteAdminFindParams = zod.object({
  id: zod.coerce.number(),
});

export const deleteAdminFindBodyReasonMax = 280;

export const DeleteAdminFindBody = zod.object({
  reason: zod.string().max(deleteAdminFindBodyReasonMax).optional(),
});

export const DeleteAdminFindResponse = zod.object({
  ok: zod.boolean(),
  findId: zod.number(),
});

/**
 * Lightweight, public, rate-limited probe used by the host-setup step of
the /create wizard so the user is told *at form-step time* that an
email is already taken instead of after the final submit. Returns
`{ taken: true }` only if a user already exists with that email
(case-insensitive). Empty / malformed emails return `{ taken: false }`
— the final create-beta submit is still the source of truth for
format and uniqueness.

 * @summary Pre-flight check whether a host email address is already in use
 */
export const checkHostEmailAvailabilityBodyEmailMax = 254;

export const CheckHostEmailAvailabilityBody = zod.object({
  email: zod
    .string()
    .max(checkHostEmailAvailabilityBodyEmailMax)
    .describe(
      "Candidate host email. Trimmed and lower-cased server-side. Empty \/ non-email returns taken=false.",
    ),
});

export const CheckHostEmailAvailabilityResponse = zod.object({
  taken: zod
    .boolean()
    .describe(
      "True iff a user already exists with this email (case-insensitive).",
    ),
});

/**
 * @summary Create a beta family with a host user, starter mission, and starter teams
 */
export const createBetaBodyFamilyNameMin = 2;
export const createBetaBodyFamilyNameMax = 60;

export const createBetaBodyMissionNameMin = 2;
export const createBetaBodyMissionNameMax = 60;

export const createBetaBodyHostDisplayNameMin = 2;
export const createBetaBodyHostDisplayNameMax = 40;

export const createBetaBodyHostUsernameMin = 2;
export const createBetaBodyHostUsernameMax = 32;

export const createBetaBodyHostUsernameRegExp = new RegExp("^[a-zA-Z0-9_]+$");
export const createBetaBodyHostPasswordMin = 6;
export const createBetaBodyHostPasswordMax = 200;

export const createBetaBodyHostEmailMax = 254;

export const CreateBetaBody = zod.object({
  familyName: zod
    .string()
    .min(createBetaBodyFamilyNameMin)
    .max(createBetaBodyFamilyNameMax),
  missionName: zod
    .string()
    .min(createBetaBodyMissionNameMin)
    .max(createBetaBodyMissionNameMax),
  hostDisplayName: zod
    .string()
    .min(createBetaBodyHostDisplayNameMin)
    .max(createBetaBodyHostDisplayNameMax),
  hostUsername: zod
    .string()
    .min(createBetaBodyHostUsernameMin)
    .max(createBetaBodyHostUsernameMax)
    .regex(createBetaBodyHostUsernameRegExp)
    .describe("letters, numbers, underscore only"),
  hostPassword: zod
    .string()
    .min(createBetaBodyHostPasswordMin)
    .max(createBetaBodyHostPasswordMax),
  hostEmail: zod
    .string()
    .email()
    .max(createBetaBodyHostEmailMax)
    .nullish()
    .describe(
      "Optional host email. Used as the recovery path for password reset and as the primary contact identity going forward. Never returned in any player-facing response. Case-insensitively unique across all hosts when set.",
    ),
  acceptedPolicies: zod
    .array(
      zod.object({
        slug: zod
          .string()
          .describe(
            "Policy slug from the registry (e.g. 'terms', 'privacy', 'ai_voice_disclosure').",
          ),
        version: zod
          .string()
          .describe("Policy version string (YYYY-MM-DD) the user accepted."),
      }),
    )
    .min(1)
    .describe(
      "Required policy acceptances. Must include all policies required at signup (terms, privacy, ai_voice_disclosure).",
    ),
});

export const CreateBetaResponse = zod.object({
  family: zod.object({
    id: zod.number(),
    slug: zod.string(),
    name: zod.string(),
    missionName: zod.string(),
  }),
  mission: zod.object({
    id: zod.number(),
    slug: zod.string(),
    name: zod.string(),
    status: zod.string(),
    totalDays: zod.number(),
  }),
  teams: zod.array(
    zod.object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
      colorHex: zod.string(),
      totem: zod.string(),
      motto: zod.string().optional(),
      score: zod.number().optional(),
    }),
  ),
  session: zod.object({
    userId: zod.number(),
    username: zod.string(),
    displayName: zod.string(),
    teamId: zod.number(),
    isAdmin: zod.boolean(),
    mustResetPassword: zod.boolean(),
    tenant: zod.object({
      id: zod.number(),
      name: zod.string(),
      slug: zod.string(),
    }),
    role: zod.string(),
    team: zod.object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
      colorHex: zod.string(),
      totem: zod.string(),
      motto: zod.string().optional(),
      score: zod.number().optional(),
    }),
  }),
});

/**
 * Returns the small preview shown to a joiner before they accept the
invite, so the join wizard can confirm "yes, this is the right
family/mission, hosted by the right person." Does NOT reveal team
rosters, member counts, or anything else beyond what's needed to
confirm the invite. Public — no session required. Rate-limited.

 * @summary Public preview of an invite (family, mission, teams, role)
 */
export const GetInvitePreviewParams = zod.object({
  code: zod.coerce.string(),
});

export const GetInvitePreviewResponse = zod
  .object({
    family: zod.object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
    }),
    missionName: zod.string().nullable(),
    mission: zod
      .object({
        id: zod.number(),
        slug: zod.string(),
        name: zod.string(),
        status: zod.string(),
        currentDay: zod.number(),
        totalDays: zod.number(),
      })
      .nullable(),
    host: zod
      .object({
        displayName: zod.string(),
      })
      .nullable(),
    teams: zod.array(
      zod.object({
        id: zod.number(),
        slug: zod.string(),
        name: zod.string(),
        colorHex: zod.string(),
        totem: zod.string(),
        motto: zod.string(),
      }),
    ),
    role: zod.enum(["player", "admin"]),
    expiresAt: zod.coerce.date().nullable(),
  })
  .describe(
    "Public preview of an invitation, shown to a joiner before they accept.\nMirrors what `GET \/invitations\/{code}` returns. Intentionally narrow:\nno rosters, no member counts, no per-team scores.\n",
  );

/**
 * @summary Redeem an invite code, create a player user, and start a session
 */
export const AcceptInviteParams = zod.object({
  code: zod.coerce.string(),
});

export const acceptInviteBodyDisplayNameMin = 2;
export const acceptInviteBodyDisplayNameMax = 40;

export const acceptInviteBodyUsernameMin = 2;
export const acceptInviteBodyUsernameMax = 32;

export const acceptInviteBodyUsernameRegExp = new RegExp("^[a-zA-Z0-9_]+$");
export const acceptInviteBodyPasswordMin = 6;
export const acceptInviteBodyPasswordMax = 200;

export const AcceptInviteBody = zod.object({
  displayName: zod
    .string()
    .min(acceptInviteBodyDisplayNameMin)
    .max(acceptInviteBodyDisplayNameMax),
  username: zod
    .string()
    .min(acceptInviteBodyUsernameMin)
    .max(acceptInviteBodyUsernameMax)
    .regex(acceptInviteBodyUsernameRegExp)
    .describe("letters, numbers, underscore only"),
  password: zod
    .string()
    .min(acceptInviteBodyPasswordMin)
    .max(acceptInviteBodyPasswordMax),
  teamId: zod.number().min(1),
  acceptedPolicies: zod
    .array(
      zod.object({
        slug: zod
          .string()
          .describe(
            "Policy slug from the registry (e.g. 'terms', 'privacy', 'ai_voice_disclosure').",
          ),
        version: zod
          .string()
          .describe("Policy version string (YYYY-MM-DD) the user accepted."),
      }),
    )
    .min(1)
    .describe(
      "Required policy acceptances. Must include all policies required at signup (terms, privacy, ai_voice_disclosure).",
    ),
});

export const AcceptInviteResponse = zod.object({
  session: zod.object({
    userId: zod.number(),
    username: zod.string(),
    displayName: zod.string(),
    teamId: zod.number(),
    isAdmin: zod.boolean(),
    mustResetPassword: zod.boolean(),
    tenant: zod.object({
      id: zod.number(),
      name: zod.string(),
      slug: zod.string(),
    }),
    role: zod.string(),
    team: zod.object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
      colorHex: zod.string(),
      totem: zod.string(),
      motto: zod.string().optional(),
      score: zod.number().optional(),
    }),
  }),
  mission: zod
    .object({
      id: zod.number(),
      slug: zod.string(),
      name: zod.string(),
    })
    .nullish(),
});

/**
 * Public. Returns the registry entries required for a given acceptance
context so the frontend can render the correct checkboxes. Pass
`context=signup|join_family|create_family|host_kids_playing|purchase|policy_update`
to get context-filtered policies. Omit to get all registry policies.

 * @summary Returns policy registry entries required for a given context
 */
export const GetLegalRequiredPoliciesQueryParams = zod.object({
  context: zod
    .enum([
      "signup",
      "join_family",
      "create_family",
      "host_kids_playing",
      "purchase",
      "policy_update",
    ])
    .optional(),
});

export const GetLegalRequiredPoliciesResponse = zod.object({
  policies: zod.array(
    zod.object({
      slug: zod.string(),
      version: zod.string(),
      effectiveDate: zod.string(),
      url: zod.string(),
      displayNameKey: zod.string(),
    }),
  ),
});

/**
 * Auth-required. Returns which required policies (signup context) the
session user has not yet accepted at the current registry version.
Use on app boot to detect whether the policy-update gate is needed.

 * @summary Returns missing required policies for the authenticated user
 */
export const GetLegalStatusResponse = zod.object({
  allClear: zod.boolean(),
  missing: zod.array(
    zod.object({
      slug: zod.string(),
      version: zod.string(),
      effectiveDate: zod.string(),
      url: zod.string(),
      displayNameKey: zod.string(),
    }),
  ),
});

/**
 * Auth-required. Records policy acceptances for the policy-update context
(existing user re-accepting after a policy version bump). Returns 422
if any slug is unknown or any version is stale.

 * @summary Record policy acceptances for an existing authenticated user
 */

export const RecordLegalAcceptancesBody = zod.object({
  acceptedPolicies: zod
    .array(
      zod.object({
        slug: zod
          .string()
          .describe(
            "Policy slug from the registry (e.g. 'terms', 'privacy', 'ai_voice_disclosure').",
          ),
        version: zod
          .string()
          .describe("Policy version string (YYYY-MM-DD) the user accepted."),
      }),
    )
    .min(1),
});

export const RecordLegalAcceptancesResponse = zod.object({
  accepted: zod.boolean(),
  count: zod.number(),
});

/**
 * Host-only. Returns the most-recent generation run, the current
narrative bundle (or null if none), the AI draft / approved clues
for the active mission, and the per-tenant daily generation
budget. The query key emitted by the generated client
(`['/api/mission-control/draft']`) is the canonical cache key
for every Mission Builder mutation to invalidate after edits,
approvals, revisions, narrative saves, and starter restores.

 * @summary Active mission's draft narrative + clues for Mission Control
 */
export const GetMissionControlDraftResponse = zod.object({
  run: zod
    .object({
      id: zod.number(),
      status: zod.string().describe("pending | completed | failed | cancelled"),
      locale: zod.string(),
      vibe: zod.string().nullish(),
      locationKind: zod.string().nullish(),
      childSafeMode: zod.boolean(),
      errorCode: zod.string().nullish(),
      errorMessageSafe: zod.string().nullish(),
      createdAt: zod.coerce.date(),
      completedAt: zod.coerce.date().nullish(),
    })
    .nullish(),
  narrative: zod
    .object({
      runId: zod.number().nullish(),
      missionTitle: zod.string(),
      storyPremise: zod.string(),
      santaIntroScript: zod.string(),
      coalieInterruptionScript: zod.string(),
      finalReveal: zod.string(),
      hostInstructions: zod.array(zod.string()),
      parentSetupNotes: zod.string(),
      locale: zod.string(),
      createdAt: zod.coerce.date(),
      isHostEdited: zod.boolean(),
      editCount: zod.number(),
      lastEditedAt: zod.coerce.date().nullish(),
      lastEditedByUserId: zod.number().nullish(),
      source: zod.string().describe("ai | host"),
      editedFields: zod.array(zod.string()),
    })
    .nullish(),
  clues: zod.array(
    zod.object({
      id: zod.number(),
      title: zod.string(),
      clue: zod.string(),
      hint: zod.string().nullish(),
      hidingSpotSuggestion: zod.string().nullish(),
      parentInstruction: zod.string().nullish(),
      safetyNotes: zod.string().nullish(),
      difficulty: zod.string(),
      points: zod.number(),
      elfSlug: zod.string(),
      sortOrder: zod.number(),
      source: zod.string(),
      approvalStatus: zod
        .string()
        .describe("draft | approved | rejected | skipped"),
      locale: zod.string().nullish(),
      isHostEdited: zod.boolean(),
      hostEditCount: zod.number(),
      lastEditedAt: zod.coerce.date().nullish(),
      isAiRevised: zod.boolean(),
      lastRevisedAt: zod.coerce.date().nullish(),
      approvedAt: zod.coerce.date().nullish(),
      rejectedAt: zod.coerce.date().nullish(),
      missionMomentKind: zod.string(),
      createdAt: zod.coerce.date(),
    }),
  ),
  storyPackSlug: zod
    .string()
    .nullish()
    .describe(
      "Currently selected story-pack arc slug, or null when none chosen.",
    ),
  rateLimit: zod.object({
    usedToday: zod.number(),
    dailyLimit: zod.number(),
    canGenerate: zod.boolean(),
  }),
});

/**
 * @summary Edit the latest draft recap
 */
export const editMissionControlRecapBodySantaLetterMin = 50;
export const editMissionControlRecapBodySantaLetterMax = 2000;

export const editMissionControlRecapBodyTitleMax = 200;

export const EditMissionControlRecapBody = zod.object({
  santaLetter: zod
    .string()
    .min(editMissionControlRecapBodySantaLetterMin)
    .max(editMissionControlRecapBodySantaLetterMax)
    .optional(),
  title: zod
    .string()
    .min(1)
    .max(editMissionControlRecapBodyTitleMax)
    .optional(),
  awards: zod.array(zod.record(zod.string(), zod.unknown())).optional(),
  selectedFindIds: zod.array(zod.number()).optional(),
});

export const EditMissionControlRecapResponse = zod.object({
  recap: zod
    .object({
      id: zod.number(),
      tenantId: zod.number(),
      missionId: zod.number(),
      createdByUserId: zod.number().nullish(),
      status: zod.enum(["draft", "published", "archived"]),
      title: zod.string().nullish(),
      santaLetter: zod.string().nullish(),
      awardsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Array of recap awards (slug + reasonShort + nullable teamId), or null on a fresh row. Validated by recapAwardsSchema server-side.",
        ),
      highlightsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Totals + per-team highlights JSON (recapHighlightsSchema), or null on a fresh row.",
        ),
      selectedFindIdsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Array of finds.id chosen for the recap (recapSelectedFindIdsSchema), or null on a fresh row.",
        ),
      mapSummaryJson: zod
        .unknown()
        .nullish()
        .describe(
          "Compact mission-progress JSON (recapMapSummarySchema), or null on a fresh row.",
        ),
      source: zod
        .enum(["generated", "starter", "host_edited"])
        .describe(
          "Where this row came from. RECAP_SOURCES in lib\/db\/src\/schema\/missionRecaps.ts is the source of truth.",
        ),
      isHostEdited: zod.boolean(),
      generatedAt: zod.coerce.date().nullish(),
      publishedAt: zod.coerce.date().nullish(),
      archivedAt: zod.coerce.date().nullish(),
      createdAt: zod.coerce.date(),
      updatedAt: zod.coerce.date(),
      locale: zod.string(),
    })
    .describe(
      "Raw `mission_recaps` row as returned by the host GET endpoint.\nMirrors the Drizzle row shape returned from `db.select().from(missionRecapsTable)`\nin `routes\/recap.ts` so the contract stays in lockstep with the\nactual server payload.\n",
    ),
});

/**
 * Host-only. Returns the latest draft recap (if any), the currently-
published recap (if any), hydrated photo cards for both, and the
eligible-finds pool used by the host's photo picker. The query key
emitted by the generated client (`['/api/mission-control/recap']`)
is the canonical cache key for every Mission Control mutation that
affects recap inputs (clues, narrative, finds, members, day index)
to invalidate after edits — the recap surface reads from those, so
any of them changing should refresh the recap card.

 * @summary Active mission's recap state for the host
 */
export const GetMissionControlRecapResponse = zod.object({
  latestDraft: zod
    .object({
      id: zod.number(),
      tenantId: zod.number(),
      missionId: zod.number(),
      createdByUserId: zod.number().nullish(),
      status: zod.enum(["draft", "published", "archived"]),
      title: zod.string().nullish(),
      santaLetter: zod.string().nullish(),
      awardsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Array of recap awards (slug + reasonShort + nullable teamId), or null on a fresh row. Validated by recapAwardsSchema server-side.",
        ),
      highlightsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Totals + per-team highlights JSON (recapHighlightsSchema), or null on a fresh row.",
        ),
      selectedFindIdsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Array of finds.id chosen for the recap (recapSelectedFindIdsSchema), or null on a fresh row.",
        ),
      mapSummaryJson: zod
        .unknown()
        .nullish()
        .describe(
          "Compact mission-progress JSON (recapMapSummarySchema), or null on a fresh row.",
        ),
      source: zod
        .enum(["generated", "starter", "host_edited"])
        .describe(
          "Where this row came from. RECAP_SOURCES in lib\/db\/src\/schema\/missionRecaps.ts is the source of truth.",
        ),
      isHostEdited: zod.boolean(),
      generatedAt: zod.coerce.date().nullish(),
      publishedAt: zod.coerce.date().nullish(),
      archivedAt: zod.coerce.date().nullish(),
      createdAt: zod.coerce.date(),
      updatedAt: zod.coerce.date(),
      locale: zod.string(),
    })
    .describe(
      "Raw `mission_recaps` row as returned by the host GET endpoint.\nMirrors the Drizzle row shape returned from `db.select().from(missionRecapsTable)`\nin `routes\/recap.ts` so the contract stays in lockstep with the\nactual server payload.\n",
    )
    .nullish(),
  latestPublished: zod
    .object({
      id: zod.number(),
      tenantId: zod.number(),
      missionId: zod.number(),
      createdByUserId: zod.number().nullish(),
      status: zod.enum(["draft", "published", "archived"]),
      title: zod.string().nullish(),
      santaLetter: zod.string().nullish(),
      awardsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Array of recap awards (slug + reasonShort + nullable teamId), or null on a fresh row. Validated by recapAwardsSchema server-side.",
        ),
      highlightsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Totals + per-team highlights JSON (recapHighlightsSchema), or null on a fresh row.",
        ),
      selectedFindIdsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Array of finds.id chosen for the recap (recapSelectedFindIdsSchema), or null on a fresh row.",
        ),
      mapSummaryJson: zod
        .unknown()
        .nullish()
        .describe(
          "Compact mission-progress JSON (recapMapSummarySchema), or null on a fresh row.",
        ),
      source: zod
        .enum(["generated", "starter", "host_edited"])
        .describe(
          "Where this row came from. RECAP_SOURCES in lib\/db\/src\/schema\/missionRecaps.ts is the source of truth.",
        ),
      isHostEdited: zod.boolean(),
      generatedAt: zod.coerce.date().nullish(),
      publishedAt: zod.coerce.date().nullish(),
      archivedAt: zod.coerce.date().nullish(),
      createdAt: zod.coerce.date(),
      updatedAt: zod.coerce.date(),
      locale: zod.string(),
    })
    .describe(
      "Raw `mission_recaps` row as returned by the host GET endpoint.\nMirrors the Drizzle row shape returned from `db.select().from(missionRecapsTable)`\nin `routes\/recap.ts` so the contract stays in lockstep with the\nactual server payload.\n",
    )
    .nullish(),
  draftPhotos: zod.array(
    zod.object({
      id: zod.number(),
      imageUrl: zod
        .string()
        .describe(
          "Object path resolved by the gated \/api\/storage\/objects\/\* route — never a long-lived signed URL.",
        ),
      roomName: zod.string().nullish(),
      teamId: zod.number().nullish(),
      teamName: zod.string().nullish(),
      huntTitle: zod.string().nullish(),
      createdAt: zod.coerce.date(),
    }),
  ),
  publishedPhotos: zod.array(
    zod.object({
      id: zod.number(),
      imageUrl: zod
        .string()
        .describe(
          "Object path resolved by the gated \/api\/storage\/objects\/\* route — never a long-lived signed URL.",
        ),
      roomName: zod.string().nullish(),
      teamId: zod.number().nullish(),
      teamName: zod.string().nullish(),
      huntTitle: zod.string().nullish(),
      createdAt: zod.coerce.date(),
    }),
  ),
  eligibleFinds: zod
    .array(
      zod.object({
        id: zod.number(),
        imageUrl: zod
          .string()
          .describe(
            "Object path resolved by the gated \/api\/storage\/objects\/\* route — never a long-lived signed URL.",
          ),
        roomName: zod.string().nullish(),
        teamId: zod.number().nullish(),
        teamName: zod.string().nullish(),
        huntTitle: zod.string().nullish(),
        createdAt: zod.coerce.date(),
      }),
    )
    .describe(
      "Eligible non-deleted finds for the active mission, newest first, capped at 200 server-side. Used by the host's Choose Photos picker.",
    ),
  hasAnyRecap: zod.boolean(),
});

/**
 * @summary Trigger a new AI mission generation run for the active mission
 */
export const generateMissionControlDraftBodyAgeRangesMax = 6;

export const generateMissionControlDraftBodyPlayerCountMax = 20;

export const generateMissionControlDraftBodyAreasAvailableItemMax = 40;

export const generateMissionControlDraftBodyAreasAvailableMax = 10;

export const generateMissionControlDraftBodyThingsToAvoidItemMax = 40;

export const generateMissionControlDraftBodyThingsToAvoidMax = 10;

export const generateMissionControlDraftBodyFamilyMagicMax = 400;

export const generateMissionControlDraftBodySafetyConstraintsConstraintsMax = 20;

export const generateMissionControlDraftBodyStoryPackSlugMax = 80;

export const generateMissionControlDraftBodyStoryPackBeatContextMax = 2000;

export const GenerateMissionControlDraftBody = zod
  .object({
    vibe: zod.enum([
      "cozy_christmas",
      "funny_elf_chaos",
      "santa_mystery",
      "competitive_teams",
      "little_kids_wonder",
      "teen_puzzle",
      "coalie_mischief",
    ]),
    locationKind: zod.enum([
      "indoor_one_home",
      "outdoor_yard",
      "neighborhood",
      "multi_home",
      "not_sure",
    ]),
    missionSize: zod.enum(["short", "standard", "big_family"]),
    ageRanges: zod
      .array(
        zod.enum(["under_5", "5_to_7", "8_to_10", "11_to_13", "teen", "adult"]),
      )
      .max(generateMissionControlDraftBodyAgeRangesMax)
      .optional(),
    playerCount: zod
      .number()
      .min(1)
      .max(generateMissionControlDraftBodyPlayerCountMax)
      .optional(),
    areasAvailable: zod
      .array(
        zod.string().max(generateMissionControlDraftBodyAreasAvailableItemMax),
      )
      .max(generateMissionControlDraftBodyAreasAvailableMax)
      .optional(),
    thingsToAvoid: zod
      .array(
        zod.string().max(generateMissionControlDraftBodyThingsToAvoidItemMax),
      )
      .max(generateMissionControlDraftBodyThingsToAvoidMax)
      .optional(),
    familyMagic: zod
      .string()
      .max(generateMissionControlDraftBodyFamilyMagicMax)
      .optional(),
    childSafeMode: zod.boolean().optional(),
    locale: zod.enum(["en", "fr-CA", "es", "pa-Guru"]).optional(),
    playEnvironment: zod
      .enum([
        "indoor_house_only",
        "outdoor_home_yard",
        "neighborhood_walk",
        "mall_or_shopping_center",
        "park_or_public_space",
        "mixed_indoor_outdoor",
        "custom",
      ])
      .optional(),
    safetyConstraints: zod
      .object({
        kidsPlaying: zod.boolean().optional(),
        constraints: zod
          .array(
            zod.enum([
              "no_bathrooms",
              "no_kitchen_heat",
              "no_roads_or_driveways",
              "no_strangers",
              "no_purchases",
              "no_climbing",
              "low_mobility",
              "stroller_friendly",
              "indoor_only_bad_weather",
              "avoid_photos_of_people",
            ]),
          )
          .max(generateMissionControlDraftBodySafetyConstraintsConstraintsMax)
          .optional(),
      })
      .optional(),
    storyPackSlug: zod
      .string()
      .max(generateMissionControlDraftBodyStoryPackSlugMax)
      .optional(),
    storyPackBeatContext: zod
      .string()
      .max(generateMissionControlDraftBodyStoryPackBeatContextMax)
      .optional(),
  })
  .describe(
    "Mirror of the server-side MissionBuilderInputs zod schema in artifacts\/api-server\/src\/lib\/aiMissionGenerator.ts. Server is the source of truth; keep enum values in sync.",
  );

export const GenerateMissionControlDraftResponse = zod
  .record(zod.string(), zod.unknown())
  .describe(
    "Result of \/mission-control\/generate. Loose passthrough — server returns generator output.",
  );

/**
 * @summary Status of a single generation run (for polling)
 */
export const GetMissionControlRunParams = zod.object({
  id: zod.coerce.number(),
});

export const GetMissionControlRunResponse = zod.object({
  id: zod.number(),
  status: zod.enum(["pending", "completed", "failed"]),
  locale: zod.string(),
  vibe: zod.string().nullish(),
  locationKind: zod.string().nullish(),
  childSafeMode: zod.boolean(),
  errorCode: zod.string().nullish(),
  errorMessageSafe: zod.string().nullish(),
  createdAt: zod.coerce.date(),
  completedAt: zod.coerce.date().nullish(),
});

/**
 * @summary Host edit of a single AI draft clue
 */
export const EditMissionControlClueParams = zod.object({
  id: zod.coerce.number(),
});

export const EditMissionControlClueBody = zod.object({
  title: zod.string().optional(),
  clue: zod.string().optional(),
  hint: zod.string().nullish(),
  hidingSpotSuggestion: zod.string().nullish(),
  parentInstruction: zod.string().nullish(),
  safetyNotes: zod.string().nullish(),
  difficulty: zod.enum(["easy", "normal", "hard"]).optional(),
  points: zod.number().optional(),
  elfSlug: zod.string().optional(),
  missionMomentKind: zod
    .enum([
      "photo_find",
      "santa_signal",
      "elf_evidence",
      "coalie_mischief",
      "kindness_mission",
      "family_pose",
      "cozy_glow",
      "memory_moment",
      "reindeer_fuel",
      "riddle_answer",
    ])
    .optional(),
});

export const EditMissionControlClueResponse = zod
  .object({
    id: zod.number(),
    title: zod.string(),
    clue: zod.string(),
    hint: zod.string().nullish(),
    hidingSpotSuggestion: zod.string().nullish(),
    parentInstruction: zod.string().nullish(),
    safetyNotes: zod.string().nullish(),
    difficulty: zod.enum(["easy", "normal", "hard"]),
    points: zod.number(),
    elfSlug: zod.string(),
    sortOrder: zod.number().optional(),
    source: zod.string(),
    approvalStatus: zod.string(),
    locale: zod.string().nullish(),
    isHostEdited: zod.boolean().optional(),
    hostEditCount: zod.number().optional(),
    lastEditedAt: zod.coerce.date().nullish(),
    isAiRevised: zod.boolean().optional(),
    lastRevisedAt: zod.coerce.date().nullish(),
    approvedAt: zod.coerce.date().nullish(),
    rejectedAt: zod.coerce.date().nullish(),
    missionMomentKind: zod.string().optional(),
    createdAt: zod.coerce.date(),
    demotedFromApproved: zod.boolean().optional(),
  })
  .describe(
    "Host-only admin projection of a hunt row used by Mission Builder.",
  );

/**
 * @summary Approve a single draft AI clue
 */
export const ApproveMissionControlClueParams = zod.object({
  id: zod.coerce.number(),
});

export const ApproveMissionControlClueResponse = zod
  .object({
    id: zod.number(),
    title: zod.string(),
    clue: zod.string(),
    hint: zod.string().nullish(),
    hidingSpotSuggestion: zod.string().nullish(),
    parentInstruction: zod.string().nullish(),
    safetyNotes: zod.string().nullish(),
    difficulty: zod.enum(["easy", "normal", "hard"]),
    points: zod.number(),
    elfSlug: zod.string(),
    sortOrder: zod.number().optional(),
    source: zod.string(),
    approvalStatus: zod.string(),
    locale: zod.string().nullish(),
    isHostEdited: zod.boolean().optional(),
    hostEditCount: zod.number().optional(),
    lastEditedAt: zod.coerce.date().nullish(),
    isAiRevised: zod.boolean().optional(),
    lastRevisedAt: zod.coerce.date().nullish(),
    approvedAt: zod.coerce.date().nullish(),
    rejectedAt: zod.coerce.date().nullish(),
    missionMomentKind: zod.string().optional(),
    createdAt: zod.coerce.date(),
    demotedFromApproved: zod.boolean().optional(),
  })
  .describe(
    "Host-only admin projection of a hunt row used by Mission Builder.",
  );

/**
 * @summary Reject a single draft AI clue
 */
export const RejectMissionControlClueParams = zod.object({
  id: zod.coerce.number(),
});

export const RejectMissionControlClueResponse = zod
  .object({
    id: zod.number(),
    title: zod.string(),
    clue: zod.string(),
    hint: zod.string().nullish(),
    hidingSpotSuggestion: zod.string().nullish(),
    parentInstruction: zod.string().nullish(),
    safetyNotes: zod.string().nullish(),
    difficulty: zod.enum(["easy", "normal", "hard"]),
    points: zod.number(),
    elfSlug: zod.string(),
    sortOrder: zod.number().optional(),
    source: zod.string(),
    approvalStatus: zod.string(),
    locale: zod.string().nullish(),
    isHostEdited: zod.boolean().optional(),
    hostEditCount: zod.number().optional(),
    lastEditedAt: zod.coerce.date().nullish(),
    isAiRevised: zod.boolean().optional(),
    lastRevisedAt: zod.coerce.date().nullish(),
    approvedAt: zod.coerce.date().nullish(),
    rejectedAt: zod.coerce.date().nullish(),
    missionMomentKind: zod.string().optional(),
    createdAt: zod.coerce.date(),
    demotedFromApproved: zod.boolean().optional(),
  })
  .describe(
    "Host-only admin projection of a hunt row used by Mission Builder.",
  );

/**
 * @summary Ask the AI to rewrite a single clue
 */
export const ReviseMissionControlClueParams = zod.object({
  id: zod.coerce.number(),
});

export const ReviseMissionControlClueBody = zod.object({
  intent: zod.string(),
  customNote: zod.string().nullish(),
  childSafeMode: zod.boolean().optional(),
});

export const ReviseMissionControlClueResponse = zod
  .record(zod.string(), zod.unknown())
  .describe(
    "Result of \/mission-control\/clues\/{id}\/revise. Loose passthrough.",
  );

/**
 * @summary Revision history for a single clue
 */
export const ListMissionControlClueRevisionsParams = zod.object({
  id: zod.coerce.number(),
});

export const ListMissionControlClueRevisionsResponse = zod.object({
  clueId: zod.number(),
  revisions: zod.array(
    zod.object({
      id: zod.number(),
      intent: zod.string(),
      outcome: zod.string(),
      childSafeMode: zod.boolean(),
      demotedFromApproved: zod.boolean(),
      createdByUserId: zod.number().nullish(),
      createdByName: zod.string().nullish(),
      createdAt: zod.coerce.date(),
      revertedFromRevisionId: zod.number().nullish(),
      previousClue: zod.record(zod.string(), zod.unknown()).nullish(),
    }),
  ),
});

/**
 * @summary Delete all revision history rows for a clue
 */
export const ClearMissionControlClueRevisionsParams = zod.object({
  id: zod.coerce.number(),
});

export const ClearMissionControlClueRevisionsResponse = zod.object({
  ok: zod.boolean(),
  deletedCount: zod.number(),
});

/**
 * @summary Delete a single revision history row
 */
export const DeleteMissionControlClueRevisionParams = zod.object({
  id: zod.coerce.number(),
  revisionId: zod.coerce.number(),
});

export const DeleteMissionControlClueRevisionResponse = zod.object({
  ok: zod.boolean(),
  deletedCount: zod.number(),
});

/**
 * @summary Revert a clue to a prior revision snapshot
 */
export const RevertMissionControlClueRevisionParams = zod.object({
  id: zod.coerce.number(),
  revisionId: zod.coerce.number(),
});

export const RevertMissionControlClueRevisionResponse = zod
  .object({
    id: zod.number(),
    title: zod.string(),
    clue: zod.string(),
    hint: zod.string().nullish(),
    hidingSpotSuggestion: zod.string().nullish(),
    parentInstruction: zod.string().nullish(),
    safetyNotes: zod.string().nullish(),
    difficulty: zod.enum(["easy", "normal", "hard"]),
    points: zod.number(),
    elfSlug: zod.string(),
    sortOrder: zod.number().optional(),
    source: zod.string(),
    approvalStatus: zod.string(),
    locale: zod.string().nullish(),
    isHostEdited: zod.boolean().optional(),
    hostEditCount: zod.number().optional(),
    lastEditedAt: zod.coerce.date().nullish(),
    isAiRevised: zod.boolean().optional(),
    lastRevisedAt: zod.coerce.date().nullish(),
    approvedAt: zod.coerce.date().nullish(),
    rejectedAt: zod.coerce.date().nullish(),
    missionMomentKind: zod.string().optional(),
    createdAt: zod.coerce.date(),
    demotedFromApproved: zod.boolean().optional(),
  })
  .describe(
    "Host-only admin projection of a hunt row used by Mission Builder.",
  );

/**
 * @summary Approve every draft AI clue and archive unplayed starter clues
 */
export const ApproveAllMissionControlCluesResponse = zod.object({
  approvedCount: zod.number(),
  starterArchived: zod.number(),
  starterKept: zod.number(),
});

/**
 * @summary Discard the current AI draft and keep the starter mission
 */
export const UseMissionControlStarterResponse = zod.object({
  rejectedCount: zod.number(),
  starterReactivated: zod.number(),
});

/**
 * @summary Edit the host narrative bundle for the active mission
 */
export const EditMissionControlNarrativeBody = zod.object({
  missionTitle: zod.string().optional(),
  storyPremise: zod.string().optional(),
  santaIntroScript: zod.string().optional(),
  coalieInterruptionScript: zod.string().optional(),
  finalReveal: zod.string().optional(),
  hostInstructions: zod.array(zod.string()).optional(),
  parentSetupNotes: zod.string().optional(),
});

export const EditMissionControlNarrativeResponse = zod.record(
  zod.string(),
  zod.unknown(),
);

/**
 * @summary Set or clear the active mission's story arc slug
 */
export const EditMissionControlStoryPackBody = zod.object({
  slug: zod.string().nullish(),
});

export const EditMissionControlStoryPackResponse = zod.object({
  ok: zod.boolean(),
  storyPackSlug: zod.string().nullish(),
});

/**
 * @summary Promote the latest draft recap to published
 */
export const PublishMissionControlRecapResponse = zod.object({
  recap: zod
    .object({
      id: zod.number(),
      tenantId: zod.number(),
      missionId: zod.number(),
      createdByUserId: zod.number().nullish(),
      status: zod.enum(["draft", "published", "archived"]),
      title: zod.string().nullish(),
      santaLetter: zod.string().nullish(),
      awardsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Array of recap awards (slug + reasonShort + nullable teamId), or null on a fresh row. Validated by recapAwardsSchema server-side.",
        ),
      highlightsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Totals + per-team highlights JSON (recapHighlightsSchema), or null on a fresh row.",
        ),
      selectedFindIdsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Array of finds.id chosen for the recap (recapSelectedFindIdsSchema), or null on a fresh row.",
        ),
      mapSummaryJson: zod
        .unknown()
        .nullish()
        .describe(
          "Compact mission-progress JSON (recapMapSummarySchema), or null on a fresh row.",
        ),
      source: zod
        .enum(["generated", "starter", "host_edited"])
        .describe(
          "Where this row came from. RECAP_SOURCES in lib\/db\/src\/schema\/missionRecaps.ts is the source of truth.",
        ),
      isHostEdited: zod.boolean(),
      generatedAt: zod.coerce.date().nullish(),
      publishedAt: zod.coerce.date().nullish(),
      archivedAt: zod.coerce.date().nullish(),
      createdAt: zod.coerce.date(),
      updatedAt: zod.coerce.date(),
      locale: zod.string(),
    })
    .describe(
      "Raw `mission_recaps` row as returned by the host GET endpoint.\nMirrors the Drizzle row shape returned from `db.select().from(missionRecapsTable)`\nin `routes\/recap.ts` so the contract stays in lockstep with the\nactual server payload.\n",
    ),
});

/**
 * @summary Pull the currently-published recap off the family page
 */
export const ArchiveMissionControlRecapResponse = zod.object({
  recap: zod
    .object({
      id: zod.number(),
      tenantId: zod.number(),
      missionId: zod.number(),
      createdByUserId: zod.number().nullish(),
      status: zod.enum(["draft", "published", "archived"]),
      title: zod.string().nullish(),
      santaLetter: zod.string().nullish(),
      awardsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Array of recap awards (slug + reasonShort + nullable teamId), or null on a fresh row. Validated by recapAwardsSchema server-side.",
        ),
      highlightsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Totals + per-team highlights JSON (recapHighlightsSchema), or null on a fresh row.",
        ),
      selectedFindIdsJson: zod
        .unknown()
        .nullish()
        .describe(
          "Array of finds.id chosen for the recap (recapSelectedFindIdsSchema), or null on a fresh row.",
        ),
      mapSummaryJson: zod
        .unknown()
        .nullish()
        .describe(
          "Compact mission-progress JSON (recapMapSummarySchema), or null on a fresh row.",
        ),
      source: zod
        .enum(["generated", "starter", "host_edited"])
        .describe(
          "Where this row came from. RECAP_SOURCES in lib\/db\/src\/schema\/missionRecaps.ts is the source of truth.",
        ),
      isHostEdited: zod.boolean(),
      generatedAt: zod.coerce.date().nullish(),
      publishedAt: zod.coerce.date().nullish(),
      archivedAt: zod.coerce.date().nullish(),
      createdAt: zod.coerce.date(),
      updatedAt: zod.coerce.date(),
      locale: zod.string(),
    })
    .describe(
      "Raw `mission_recaps` row as returned by the host GET endpoint.\nMirrors the Drizzle row shape returned from `db.select().from(missionRecapsTable)`\nin `routes\/recap.ts` so the contract stays in lockstep with the\nactual server payload.\n",
    ),
});

/**
 * @summary Family-facing published recap for the active mission
 */
export const GetRecapCurrentResponse = zod.object({
  status: zod.enum(["none", "published"]),
  recap: zod
    .object({
      title: zod.string().nullish(),
      santaLetter: zod.string().nullish(),
      publishedAt: zod.coerce.date().nullish(),
      locale: zod.string().optional(),
      awards: zod
        .array(
          zod.object({
            slug: zod.string(),
            teamId: zod.number().nullish(),
            teamName: zod.string().nullish(),
            reasonShort: zod.string(),
          }),
        )
        .optional(),
      highlights: zod
        .object({
          totals: zod
            .object({
              finds: zod.number().optional(),
              days: zod.number().optional(),
              teams: zod.number().optional(),
            })
            .optional(),
          perTeam: zod
            .array(
              zod.object({
                teamId: zod.number().optional(),
                teamName: zod.string().optional(),
                finds: zod.number().optional(),
              }),
            )
            .optional(),
        })
        .nullish(),
      mapSummary: zod
        .object({
          totalFinds: zod.number().optional(),
          lastFindAt: zod.string().nullish(),
          currentDay: zod.number().optional(),
          totalDays: zod.number().optional(),
        })
        .nullish(),
      photos: zod
        .array(
          zod.object({
            id: zod.number(),
            imageUrl: zod
              .string()
              .describe(
                "Object path resolved by the gated \/api\/storage\/objects\/\* route — never a long-lived signed URL.",
              ),
            roomName: zod.string().nullish(),
            teamId: zod.number().nullish(),
            teamName: zod.string().nullish(),
            huntTitle: zod.string().nullish(),
            createdAt: zod.coerce.date(),
          }),
        )
        .optional(),
    })
    .nullish(),
  privacy: zod
    .object({
      note: zod.string().optional(),
      publicShareEnabled: zod.boolean().optional(),
    })
    .optional(),
});

/**
 * Host-only. Records a short (≤280 char) plain-text announcement
for the host's active mission. The announcement appears in the
Fireplace Feed for every team in the family within ≤5 s (the
hub aggregation polls on that cadence). Players (role=player)
receive 403.

Tenant + mission are derived server-side from the session — the
client never supplies them. Each call also writes a
host_announcement_posted row to admin_audit_events.

 * @summary Host posts a short announcement to the Fireplace Feed
 */
export const postHostAnnouncementBodyBodyMax = 280;

export const PostHostAnnouncementBody = zod.object({
  body: zod
    .string()
    .min(1)
    .max(postHostAnnouncementBodyBodyMax)
    .describe("Plain-text announcement, ≤280 characters."),
});

/**
 * Host-only. Soft-deletes an announcement the host previously posted
in the active mission. Tenant + mission scoped server-side; a host
cannot delete announcements from another family. Records a
host_announcement_deleted audit row.

 * @summary Host deletes one of their own announcements
 */
export const DeleteHostAnnouncementParams = zod.object({
  announcementId: zod.coerce.number(),
});

/**
 * Returns the full payload for the Christmas Hub v1 home page:
hero (family/mission name, day, sleigh countdown), deterministic
Daily Magic content key, the last 5 Fireplace Feed messages from
the elf inbox, Memory Shelf links (recap gated on published status),
an Activity Snapshot (finds, clues, unread counts), and (when the
active mission has a story pack selected) the current story
chapter for today's game day.

All data is scoped server-side via session + tenant + mission — the
client never supplies IDs. Photo URLs are returned through the gated
/api/storage/objects/* route; raw object paths never appear in the
response. Draft clue text, host-only narrative fields, and invite
codes are excluded.

 * @summary Family Fireplace aggregation for the signed-in player's active mission
 */
export const GetHubCurrentQueryParams = zod.object({
  locale: zod.coerce
    .string()
    .optional()
    .describe(
      'Optional BCP-47 locale used to resolve the story chapter\'s title and\npremise. Defaults to \"en\" when omitted or unsupported.\n',
    ),
});

export const getHubCurrentResponseActivityTrendsFindsMin = 7;
export const getHubCurrentResponseActivityTrendsFindsMax = 7;

export const getHubCurrentResponseActivityTrendsCluesMin = 7;
export const getHubCurrentResponseActivityTrendsCluesMax = 7;

export const getHubCurrentResponseActivityTrendsUnreadMin = 7;
export const getHubCurrentResponseActivityTrendsUnreadMax = 7;

export const GetHubCurrentResponse = zod.object({
  hero: zod.object({
    teamName: zod.string(),
    teamMotto: zod.string().nullable(),
    teamColorHex: zod.string(),
    teamScore: zod.number(),
    missionName: zod.string().nullish(),
    missionDay: zod.number(),
    totalDays: zod.number(),
    sleighEtaSeconds: zod
      .number()
      .describe("Seconds until sleigh arrival (clamped to 0 when in the past)"),
  }),
  dailyMagic: zod.object({
    key: zod
      .string()
      .describe(
        "Deterministic i18n content key. Maps to dailyMagic.<key>.{headline,body} client-side.\nPossible values: welcomeDay1 | firstClue | newMessage | elfEncouragement |\ncoalieMischief | familySpark | santaSignal | idleWarm | recapReady\n",
      ),
    missionDay: zod.number(),
    headline: zod
      .string()
      .nullish()
      .describe(
        "Optional AI-generated headline that reflects what the family actually\ndid today. When present, the client should prefer this over the i18n\nstring for `dailyMagic.<key>.headline`. Null when generation hasn't\nrun yet, failed, was skipped (completed missions), or fell back to\nthe deterministic catalog.\n",
      ),
    body: zod
      .string()
      .nullish()
      .describe(
        "Optional AI-generated body paired with `headline`. Same fallback\nsemantics — when null, render `dailyMagic.<key>.body`.\n",
      ),
  }),
  feed: zod.array(
    zod.object({
      id: zod.number(),
      source: zod
        .string()
        .describe(
          '\"elf\" — an elf inbox message (links to \/inbox\/:id).\n\"host\" — a host announcement (links nowhere; can be deleted by hosts).\n',
        ),
      elfSlug: zod
        .string()
        .describe("For host announcements, the literal string 'host'."),
      elfName: zod
        .string()
        .describe("Author display name. Elf name or host display name."),
      kind: zod
        .string()
        .describe(
          "narrative | hint | praise | warning | postcard | announcement",
        ),
      title: zod.string().nullish(),
      body: zod.string(),
      isRead: zod
        .boolean()
        .describe(
          "Always true for host announcements (read-state is per-team and only tracked for elf inbox).",
        ),
      createdAt: zod.coerce.date(),
      canDelete: zod
        .boolean()
        .optional()
        .describe(
          "True when the signed-in user is a host of this family and the item is a host announcement they can delete.",
        ),
    }),
  ),
  memoryShelf: zod.object({
    hasPublishedRecap: zod.boolean(),
    recapTitle: zod.string().nullish(),
    recapPublishedAt: zod.coerce.date().nullish(),
    recentPhotoUrl: zod
      .string()
      .nullish()
      .describe(
        "Gated \/api\/storage\/objects\/\* URL — never a raw object path.",
      ),
    missionStatus: zod
      .string()
      .describe("active | draft | completed | archived"),
  }),
  activity: zod.object({
    findsCount: zod.number(),
    approvedCluesTotal: zod.number(),
    unreadCount: zod.number(),
    trends: zod
      .object({
        finds: zod
          .array(zod.number())
          .min(getHubCurrentResponseActivityTrendsFindsMin)
          .max(getHubCurrentResponseActivityTrendsFindsMax)
          .describe(
            "Verified, non-deleted finds per day for the signed-in team.",
          ),
        clues: zod
          .array(zod.number())
          .min(getHubCurrentResponseActivityTrendsCluesMin)
          .max(getHubCurrentResponseActivityTrendsCluesMax)
          .describe("Hunts approved per day across the active mission."),
        unread: zod
          .array(zod.number())
          .min(getHubCurrentResponseActivityTrendsUnreadMin)
          .max(getHubCurrentResponseActivityTrendsUnreadMax)
          .describe(
            "Inbox messages received per day for the signed-in team (proxy for daily message activity).",
          ),
      })
      .describe(
        "Per-day activity buckets for the last 7 days, oldest→newest. Each array has exactly 7 integer entries. Buckets are computed in UTC and span the 7 calendar days ending today (inclusive).",
      ),
  }),
  chapter: zod
    .union([
      zod.object({
        packSlug: zod
          .string()
          .describe("Story pack slug, e.g. 'classic-north-pole'."),
        packTitle: zod.string().describe("Localized pack title."),
        dayOffset: zod
          .number()
          .describe(
            "1-based day offset of the resolved beat (matches the mission's current game day when a beat is found).",
          ),
        totalBeats: zod
          .number()
          .describe("Number of dailyBeats in the pack (excludes the finale)."),
        isFinale: zod
          .boolean()
          .describe(
            "True when the resolved beat is the pack's finaleBeat (only possible when no daily beat matches today's offset and the finale offset matches).",
          ),
        beatTitle: zod
          .string()
          .describe("Localized beat title — e.g. 'The Tangled Tinsel Trap'."),
        beatPlayerPremise: zod
          .string()
          .describe(
            "Localized, player-safe premise for today's beat. Host-only guidance is never included.",
          ),
      }),
      zod.null(),
    ])
    .describe(
      "Today's story chapter when the active mission has a story pack\nselected and a beat exists for the current game day. Null when\nno story pack is set, the pack slug doesn't resolve, or no beat\nmatches the current day.\n",
    ),
});

/**
 * Returns the server's VAPID public key. Safe to expose publicly. Returns configured=false when VAPID is not set up.
 * @summary Get the VAPID public key (unauthenticated)
 */
export const GetNotificationPushPublicKeyResponse = zod.object({
  publicKey: zod.string().nullish(),
  configured: zod.boolean(),
});

/**
 * @summary Get the signed-in user's notification preferences
 */
export const GetNotificationPreferencesResponse = zod.object({
  pushEnabled: zod.boolean(),
  emailEnabled: zod.boolean().nullable(),
  quietHoursStart: zod.string().nullable().describe("HH:MM 24-hour format"),
  quietHoursEnd: zod.string().nullable().describe("HH:MM 24-hour format"),
  channelsJson: zod.string().describe("JSON-encoded per-channel preferences"),
  updatedAt: zod.coerce.date(),
});

/**
 * @summary Update the signed-in user's notification preferences
 */
export const updateNotificationPreferencesBodyQuietHoursStartRegExp =
  new RegExp("^\\d{2}:\\d{2}$");
export const updateNotificationPreferencesBodyQuietHoursEndRegExp = new RegExp(
  "^\\d{2}:\\d{2}$",
);

export const UpdateNotificationPreferencesBody = zod.object({
  pushEnabled: zod.boolean().optional(),
  emailEnabled: zod.boolean().nullish(),
  quietHoursStart: zod
    .string()
    .regex(updateNotificationPreferencesBodyQuietHoursStartRegExp)
    .nullish(),
  quietHoursEnd: zod
    .string()
    .regex(updateNotificationPreferencesBodyQuietHoursEndRegExp)
    .nullish(),
  channelsJson: zod.string().optional(),
});

export const UpdateNotificationPreferencesResponse = zod.object({
  pushEnabled: zod.boolean(),
  emailEnabled: zod.boolean().nullable(),
  quietHoursStart: zod.string().nullable().describe("HH:MM 24-hour format"),
  quietHoursEnd: zod.string().nullable().describe("HH:MM 24-hour format"),
  channelsJson: zod.string().describe("JSON-encoded per-channel preferences"),
  updatedAt: zod.coerce.date(),
});

/**
 * Stores the Web Push subscription server-side and enables pushEnabled in preferences.
endpoint, p256dh, and auth are NEVER returned to any client response.

 * @summary Register a Web Push subscription for this browser/device
 */
export const subscribePushBodyEndpointMax = 2048;

export const subscribePushBodyP256dhMax = 512;

export const subscribePushBodyAuthMax = 256;

export const subscribePushBodyUserAgentSummaryMax = 120;

export const SubscribePushBody = zod.object({
  endpoint: zod.string().url().max(subscribePushBodyEndpointMax),
  p256dh: zod.string().max(subscribePushBodyP256dhMax),
  auth: zod.string().max(subscribePushBodyAuthMax),
  userAgentSummary: zod
    .string()
    .max(subscribePushBodyUserAgentSummaryMax)
    .optional(),
});

/**
 * @summary Revoke a Web Push subscription
 */
export const unsubscribePushBodyEndpointMax = 2048;

export const UnsubscribePushBody = zod.object({
  endpoint: zod.string().url().max(unsubscribePushBodyEndpointMax),
});

export const UnsubscribePushResponse = zod.object({
  ok: zod.boolean(),
});

/**
 * Stores a native push device token (Apple APNs or Google FCM) server-side
and enables pushEnabled in preferences. The raw deviceToken is NEVER
returned to any client response.

 * @summary Register a native push device token (APNs / FCM)
 */
export const subscribePushNativeBodyDeviceTokenMin = 8;
export const subscribePushNativeBodyDeviceTokenMax = 512;

export const subscribePushNativeBodyUserAgentSummaryMax = 120;

export const SubscribePushNativeBody = zod.object({
  deviceToken: zod
    .string()
    .min(subscribePushNativeBodyDeviceTokenMin)
    .max(subscribePushNativeBodyDeviceTokenMax)
    .describe(
      "Raw native push device token. For iOS APNs this is the 64-hex-char\ntoken. Never logged in plaintext server-side.\n",
    ),
  platform: zod
    .enum(["ios", "android"])
    .describe("Native platform that produced the token."),
  userAgentSummary: zod
    .string()
    .max(subscribePushNativeBodyUserAgentSummaryMax)
    .optional(),
});

/**
 * @summary Revoke a native push device token
 */
export const unsubscribePushNativeBodyDeviceTokenMin = 8;
export const unsubscribePushNativeBodyDeviceTokenMax = 512;

export const UnsubscribePushNativeBody = zod.object({
  deviceToken: zod
    .string()
    .min(unsubscribePushNativeBodyDeviceTokenMin)
    .max(unsubscribePushNativeBodyDeviceTokenMax),
});

export const UnsubscribePushNativeResponse = zod.object({
  ok: zod.boolean(),
});

/**
 * @summary Send a test notification to the signed-in adult user (rate-limited 5/hr)
 */
export const TestSendSelfResponse = zod.object({
  ok: zod.boolean(),
});

/**
 * @summary List the signed-in user's in-app notifications (cursor-paginated)
 */
export const listInAppNotificationsQueryLimitDefault = 20;
export const listInAppNotificationsQueryLimitMax = 50;

export const ListInAppNotificationsQueryParams = zod.object({
  before: zod.coerce.number().optional(),
  limit: zod.coerce
    .number()
    .min(1)
    .max(listInAppNotificationsQueryLimitMax)
    .default(listInAppNotificationsQueryLimitDefault),
  status: zod.enum(["unread", "read", "archived"]).optional(),
});

export const ListInAppNotificationsResponse = zod.object({
  items: zod.array(
    zod.object({
      id: zod.number(),
      channel: zod.string(),
      title: zod.string(),
      body: zod.string(),
      status: zod.enum(["unread", "read", "archived"]),
      actionHref: zod.string().nullable(),
      createdAt: zod.coerce.date(),
      readAt: zod.coerce.date().nullable(),
    }),
  ),
  hasMore: zod.boolean(),
  nextCursor: zod.number().nullable(),
});

/**
 * @summary Mark a single in-app notification as read
 */
export const MarkInAppNotificationReadParams = zod.object({
  id: zod.coerce.number(),
});

export const MarkInAppNotificationReadResponse = zod.object({
  id: zod.number(),
  channel: zod.string(),
  title: zod.string(),
  body: zod.string(),
  status: zod.enum(["unread", "read", "archived"]),
  actionHref: zod.string().nullable(),
  createdAt: zod.coerce.date(),
  readAt: zod.coerce.date().nullable(),
});

/**
 * @summary Mark all unread in-app notifications as read for the current user
 */
export const MarkAllInAppNotificationsReadResponse = zod.object({
  ok: zod.boolean(),
});

/**
 * Adult roles [owner, admin, player] only. child role returns 403.
Hosts see all claims; non-hosts see only their own claims.
privateNote is returned only to the claimant or hosts.

 * @summary List gift claims visible to the current user
 */
export const ListGiftClaimsResponseItem = zod
  .object({
    id: zod.number(),
    wishId: zod.number(),
    claimantUserId: zod.number(),
    claimantDisplayName: zod.string().nullable(),
    status: zod.enum([
      "claimed",
      "purchased",
      "wrapped",
      "ready",
      "released",
      "archived",
    ]),
    privateNote: zod.string().nullable(),
    estimatedPriceCents: zod.number().nullable(),
    storeHint: zod.string().nullable(),
    dueBy: zod.coerce.date().nullable(),
    hiddenFromWishOwner: zod.boolean(),
    createdAt: zod.coerce.date(),
    updatedAt: zod.coerce.date(),
  })
  .and(
    zod.object({
      wishTitle: zod.string(),
      wishPriority: zod.enum(["low", "normal", "top"]),
      wishCategory: zod
        .enum(["toy", "book", "clothing", "experience", "game", "other"])
        .nullish(),
      wishOwnerUserId: zod.number(),
    }),
  );
export const ListGiftClaimsResponse = zod.array(ListGiftClaimsResponseItem);

/**
 * Creates an active gift claim. Blocks self-claiming. Returns 409 if an
active claim already exists unless hostOverrideDuplicate=true and caller is a host.

 * @summary Claim a family member's wish
 */
export const createGiftClaimBodyPrivateNoteMax = 2000;

export const createGiftClaimBodyEstimatedPriceCentsMin = 0;

export const createGiftClaimBodyStoreHintMax = 200;

export const CreateGiftClaimBody = zod.object({
  wishId: zod.number(),
  privateNote: zod.string().max(createGiftClaimBodyPrivateNoteMax).optional(),
  estimatedPriceCents: zod
    .number()
    .min(createGiftClaimBodyEstimatedPriceCentsMin)
    .optional(),
  storeHint: zod.string().max(createGiftClaimBodyStoreHintMax).optional(),
  dueBy: zod.coerce.date().optional(),
  hostOverrideDuplicate: zod.boolean().optional(),
});

/**
 * @summary Update a gift claim's status or private note
 */
export const UpdateGiftClaimParams = zod.object({
  id: zod.coerce.number(),
});

export const updateGiftClaimBodyPrivateNoteMax = 2000;

export const updateGiftClaimBodyEstimatedPriceCentsMin = 0;

export const updateGiftClaimBodyStoreHintMax = 200;

export const UpdateGiftClaimBody = zod.object({
  status: zod
    .enum(["claimed", "purchased", "wrapped", "ready", "released", "archived"])
    .optional(),
  privateNote: zod.string().max(updateGiftClaimBodyPrivateNoteMax).nullish(),
  estimatedPriceCents: zod
    .number()
    .min(updateGiftClaimBodyEstimatedPriceCentsMin)
    .nullish(),
  storeHint: zod.string().max(updateGiftClaimBodyStoreHintMax).nullish(),
  dueBy: zod.coerce.date().nullish(),
});

export const UpdateGiftClaimResponse = zod.object({
  id: zod.number(),
  wishId: zod.number(),
  claimantUserId: zod.number(),
  claimantDisplayName: zod.string().nullable(),
  status: zod.enum([
    "claimed",
    "purchased",
    "wrapped",
    "ready",
    "released",
    "archived",
  ]),
  privateNote: zod.string().nullable(),
  estimatedPriceCents: zod.number().nullable(),
  storeHint: zod.string().nullable(),
  dueBy: zod.coerce.date().nullable(),
  hiddenFromWishOwner: zod.boolean(),
  createdAt: zod.coerce.date(),
  updatedAt: zod.coerce.date(),
});

/**
 * @summary Release a gift claim (make the wish claimable again)
 */
export const ReleaseGiftClaimParams = zod.object({
  id: zod.coerce.number(),
});

export const ReleaseGiftClaimResponse = zod.object({
  id: zod.number(),
  wishId: zod.number(),
  claimantUserId: zod.number(),
  claimantDisplayName: zod.string().nullable(),
  status: zod.enum([
    "claimed",
    "purchased",
    "wrapped",
    "ready",
    "released",
    "archived",
  ]),
  privateNote: zod.string().nullable(),
  estimatedPriceCents: zod.number().nullable(),
  storeHint: zod.string().nullable(),
  dueBy: zod.coerce.date().nullable(),
  hiddenFromWishOwner: zod.boolean(),
  createdAt: zod.coerce.date(),
  updatedAt: zod.coerce.date(),
});

/**
 * Returns counts of unclaimed/claimed/purchased/wrapped/ready wishes and conflict flags for owner/admin roles only.
 * @summary Host-only aggregate stats for the gift workshop
 */
export const GetGiftWorkshopOverviewResponse = zod.object({
  totalWishes: zod.number(),
  unclaimed: zod.number(),
  claimed: zod.number(),
  purchased: zod.number(),
  wrapped: zod.number(),
  ready: zod.number(),
  needsAttention: zod.number(),
  duplicateConflicts: zod.number(),
  membersWithNoWishesCount: zod.number(),
});

/**
 * Returns all wishes owned by the current user in the active tenant,
ordered by sortOrder ascending then createdAt descending. Active
wishes (archivedAt IS NULL) are returned by default. Pass
`?includeArchived=true` to also include archived rows so the owner
can review their full history.

Tenant is always resolved server-side from the session — clients
never supply tenantId.

 * @summary List the signed-in user's own wishes
 */
export const ListMyWishesQueryParams = zod.object({
  includeArchived: zod.coerce.boolean().optional(),
});

export const ListMyWishesResponseItem = zod.object({
  id: zod.number(),
  ownerUserId: zod.number(),
  title: zod.string(),
  description: zod.string().nullish(),
  priority: zod.enum(["low", "normal", "top"]),
  category: zod
    .enum(["toy", "book", "clothing", "experience", "game", "other"])
    .nullish(),
  sortOrder: zod.number(),
  archivedAt: zod.coerce.date().nullish(),
  purchasedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Host-only. Set when an admin marks the wish as purchased via the\nGift Workshop. Always omitted from non-admin endpoints so\nchildren\/players can't infer who is getting what for Christmas.\n",
    ),
  wrappedAt: zod.coerce
    .date()
    .nullish()
    .describe("Host-only. Set when an admin marks the wish as wrapped."),
  budgetCents: zod
    .number()
    .nullish()
    .describe(
      "Host-only. Estimated spend for this wish in integer cents\n(e.g. $24.99 → 2499). Always null on non-admin endpoints.\n",
    ),
  claimedByUserId: zod
    .number()
    .nullish()
    .describe(
      "User id of the family member who has claimed this wish. Null\nwhen unclaimed. Omitted entirely from responses to the wish\nowner so they can't tell whether anyone has claimed it.\n",
    ),
  claimedByDisplayName: zod
    .string()
    .nullish()
    .describe(
      "Display name of the claimer, denormalized for convenience.\nOmitted from responses to the wish owner.\n",
    ),
  claimedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Timestamp the wish was claimed. Omitted from responses to\nthe wish owner.\n",
    ),
  createdAt: zod.coerce.date(),
  updatedAt: zod.coerce.date(),
});
export const ListMyWishesResponse = zod.array(ListMyWishesResponseItem);

/**
 * Returns every active (non-revoked) member of the current tenant, each
with their non-archived wish list. Read-only for all roles including
child. Tenant is resolved server-side from the session.

 * @summary List all active family members with their non-archived wishes
 */
export const ListFamilyWishesResponseItem = zod.object({
  userId: zod.number(),
  displayName: zod.string(),
  role: zod.string().describe("owner | admin | player | child"),
  wishes: zod.array(
    zod.object({
      id: zod.number(),
      ownerUserId: zod.number(),
      title: zod.string(),
      description: zod.string().nullish(),
      priority: zod.enum(["low", "normal", "top"]),
      category: zod
        .enum(["toy", "book", "clothing", "experience", "game", "other"])
        .nullish(),
      sortOrder: zod.number(),
      archivedAt: zod.coerce.date().nullish(),
      purchasedAt: zod.coerce
        .date()
        .nullish()
        .describe(
          "Host-only. Set when an admin marks the wish as purchased via the\nGift Workshop. Always omitted from non-admin endpoints so\nchildren\/players can't infer who is getting what for Christmas.\n",
        ),
      wrappedAt: zod.coerce
        .date()
        .nullish()
        .describe("Host-only. Set when an admin marks the wish as wrapped."),
      budgetCents: zod
        .number()
        .nullish()
        .describe(
          "Host-only. Estimated spend for this wish in integer cents\n(e.g. $24.99 → 2499). Always null on non-admin endpoints.\n",
        ),
      claimedByUserId: zod
        .number()
        .nullish()
        .describe(
          "User id of the family member who has claimed this wish. Null\nwhen unclaimed. Omitted entirely from responses to the wish\nowner so they can't tell whether anyone has claimed it.\n",
        ),
      claimedByDisplayName: zod
        .string()
        .nullish()
        .describe(
          "Display name of the claimer, denormalized for convenience.\nOmitted from responses to the wish owner.\n",
        ),
      claimedAt: zod.coerce
        .date()
        .nullish()
        .describe(
          "Timestamp the wish was claimed. Omitted from responses to\nthe wish owner.\n",
        ),
      createdAt: zod.coerce.date(),
      updatedAt: zod.coerce.date(),
    }),
  ),
});
export const ListFamilyWishesResponse = zod.array(ListFamilyWishesResponseItem);

/**
 * @summary Add a wish to the signed-in user's Christmas list
 */
export const createWishBodyTitleMax = 200;

export const createWishBodyDescriptionMax = 1000;

export const CreateWishBody = zod.object({
  title: zod.string().min(1).max(createWishBodyTitleMax),
  description: zod.string().max(createWishBodyDescriptionMax).nullish(),
  priority: zod.enum(["low", "normal", "top"]).nullish(),
  category: zod
    .enum(["toy", "book", "clothing", "experience", "game", "other"])
    .nullish(),
});

/**
 * Only the wish owner may mutate their wish. All fields are optional
(PATCH semantics). Cross-tenant access returns 404 to avoid existence
leaks. Updating another user's wish returns 404 (not 403) for the
same reason.

 * @summary Update a wish owned by the signed-in user
 */
export const UpdateWishParams = zod.object({
  id: zod.coerce.number(),
});

export const updateWishBodyTitleMax = 200;

export const updateWishBodyDescriptionMax = 1000;

export const UpdateWishBody = zod.object({
  title: zod.string().min(1).max(updateWishBodyTitleMax).optional(),
  description: zod.string().max(updateWishBodyDescriptionMax).nullish(),
  priority: zod.enum(["low", "normal", "top"]).nullish(),
  category: zod
    .enum(["toy", "book", "clothing", "experience", "game", "other"])
    .nullish(),
});

export const UpdateWishResponse = zod.object({
  id: zod.number(),
  ownerUserId: zod.number(),
  title: zod.string(),
  description: zod.string().nullish(),
  priority: zod.enum(["low", "normal", "top"]),
  category: zod
    .enum(["toy", "book", "clothing", "experience", "game", "other"])
    .nullish(),
  sortOrder: zod.number(),
  archivedAt: zod.coerce.date().nullish(),
  purchasedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Host-only. Set when an admin marks the wish as purchased via the\nGift Workshop. Always omitted from non-admin endpoints so\nchildren\/players can't infer who is getting what for Christmas.\n",
    ),
  wrappedAt: zod.coerce
    .date()
    .nullish()
    .describe("Host-only. Set when an admin marks the wish as wrapped."),
  budgetCents: zod
    .number()
    .nullish()
    .describe(
      "Host-only. Estimated spend for this wish in integer cents\n(e.g. $24.99 → 2499). Always null on non-admin endpoints.\n",
    ),
  claimedByUserId: zod
    .number()
    .nullish()
    .describe(
      "User id of the family member who has claimed this wish. Null\nwhen unclaimed. Omitted entirely from responses to the wish\nowner so they can't tell whether anyone has claimed it.\n",
    ),
  claimedByDisplayName: zod
    .string()
    .nullish()
    .describe(
      "Display name of the claimer, denormalized for convenience.\nOmitted from responses to the wish owner.\n",
    ),
  claimedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Timestamp the wish was claimed. Omitted from responses to\nthe wish owner.\n",
    ),
  createdAt: zod.coerce.date(),
  updatedAt: zod.coerce.date(),
});

/**
 * Sets archivedAt to the current timestamp. Only the wish owner may
archive their own wish. Cross-tenant and cross-owner access both
return 404.

 * @summary Archive a wish owned by the signed-in user
 */
export const ArchiveWishParams = zod.object({
  id: zod.coerce.number(),
});

export const ArchiveWishResponse = zod.object({
  id: zod.number(),
  ownerUserId: zod.number(),
  title: zod.string(),
  description: zod.string().nullish(),
  priority: zod.enum(["low", "normal", "top"]),
  category: zod
    .enum(["toy", "book", "clothing", "experience", "game", "other"])
    .nullish(),
  sortOrder: zod.number(),
  archivedAt: zod.coerce.date().nullish(),
  purchasedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Host-only. Set when an admin marks the wish as purchased via the\nGift Workshop. Always omitted from non-admin endpoints so\nchildren\/players can't infer who is getting what for Christmas.\n",
    ),
  wrappedAt: zod.coerce
    .date()
    .nullish()
    .describe("Host-only. Set when an admin marks the wish as wrapped."),
  budgetCents: zod
    .number()
    .nullish()
    .describe(
      "Host-only. Estimated spend for this wish in integer cents\n(e.g. $24.99 → 2499). Always null on non-admin endpoints.\n",
    ),
  claimedByUserId: zod
    .number()
    .nullish()
    .describe(
      "User id of the family member who has claimed this wish. Null\nwhen unclaimed. Omitted entirely from responses to the wish\nowner so they can't tell whether anyone has claimed it.\n",
    ),
  claimedByDisplayName: zod
    .string()
    .nullish()
    .describe(
      "Display name of the claimer, denormalized for convenience.\nOmitted from responses to the wish owner.\n",
    ),
  claimedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Timestamp the wish was claimed. Omitted from responses to\nthe wish owner.\n",
    ),
  createdAt: zod.coerce.date(),
  updatedAt: zod.coerce.date(),
});

/**
 * Returns the non-archived wishes belonging to a single family member.
The member must be an active (non-revoked) participant of the caller's
tenant — otherwise the server returns 404 so the API never leaks
whether a userId exists in another family's data.

 * @summary View one family member's wish list (read-only)
 */
export const ListMemberWishesParams = zod.object({
  userId: zod.coerce.number(),
});

export const ListMemberWishesResponseItem = zod.object({
  id: zod.number(),
  ownerUserId: zod.number(),
  title: zod.string(),
  description: zod.string().nullish(),
  priority: zod.enum(["low", "normal", "top"]),
  category: zod
    .enum(["toy", "book", "clothing", "experience", "game", "other"])
    .nullish(),
  sortOrder: zod.number(),
  archivedAt: zod.coerce.date().nullish(),
  purchasedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Host-only. Set when an admin marks the wish as purchased via the\nGift Workshop. Always omitted from non-admin endpoints so\nchildren\/players can't infer who is getting what for Christmas.\n",
    ),
  wrappedAt: zod.coerce
    .date()
    .nullish()
    .describe("Host-only. Set when an admin marks the wish as wrapped."),
  budgetCents: zod
    .number()
    .nullish()
    .describe(
      "Host-only. Estimated spend for this wish in integer cents\n(e.g. $24.99 → 2499). Always null on non-admin endpoints.\n",
    ),
  claimedByUserId: zod
    .number()
    .nullish()
    .describe(
      "User id of the family member who has claimed this wish. Null\nwhen unclaimed. Omitted entirely from responses to the wish\nowner so they can't tell whether anyone has claimed it.\n",
    ),
  claimedByDisplayName: zod
    .string()
    .nullish()
    .describe(
      "Display name of the claimer, denormalized for convenience.\nOmitted from responses to the wish owner.\n",
    ),
  claimedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Timestamp the wish was claimed. Omitted from responses to\nthe wish owner.\n",
    ),
  createdAt: zod.coerce.date(),
  updatedAt: zod.coerce.date(),
});
export const ListMemberWishesResponse = zod.array(ListMemberWishesResponseItem);

/**
 * Owner/admin only. Returns every active family member with their
non-archived wishes, including host-only planning fields
(purchasedAt, wrappedAt, budgetCents) and a budget summary that
compares estimated total spend (sum of budgetCents) against
actual spend (sum of budgetCents on wishes whose purchasedAt is
set). Children and players cannot reach this endpoint — the
server returns 403.

 * @summary Admin Gift Workshop — every family member's wishes with host-only fields
 */
export const GetGiftWorkshopResponse = zod.object({
  members: zod.array(
    zod.object({
      userId: zod.number(),
      displayName: zod.string(),
      role: zod.string().describe("owner | admin | player | child"),
      wishes: zod.array(
        zod.object({
          id: zod.number(),
          ownerUserId: zod.number(),
          title: zod.string(),
          description: zod.string().nullish(),
          priority: zod.enum(["low", "normal", "top"]),
          category: zod
            .enum(["toy", "book", "clothing", "experience", "game", "other"])
            .nullish(),
          sortOrder: zod.number(),
          archivedAt: zod.coerce.date().nullish(),
          purchasedAt: zod.coerce
            .date()
            .nullish()
            .describe(
              "Host-only. Set when an admin marks the wish as purchased via the\nGift Workshop. Always omitted from non-admin endpoints so\nchildren\/players can't infer who is getting what for Christmas.\n",
            ),
          wrappedAt: zod.coerce
            .date()
            .nullish()
            .describe(
              "Host-only. Set when an admin marks the wish as wrapped.",
            ),
          budgetCents: zod
            .number()
            .nullish()
            .describe(
              "Host-only. Estimated spend for this wish in integer cents\n(e.g. $24.99 → 2499). Always null on non-admin endpoints.\n",
            ),
          claimedByUserId: zod
            .number()
            .nullish()
            .describe(
              "User id of the family member who has claimed this wish. Null\nwhen unclaimed. Omitted entirely from responses to the wish\nowner so they can't tell whether anyone has claimed it.\n",
            ),
          claimedByDisplayName: zod
            .string()
            .nullish()
            .describe(
              "Display name of the claimer, denormalized for convenience.\nOmitted from responses to the wish owner.\n",
            ),
          claimedAt: zod.coerce
            .date()
            .nullish()
            .describe(
              "Timestamp the wish was claimed. Omitted from responses to\nthe wish owner.\n",
            ),
          createdAt: zod.coerce.date(),
          updatedAt: zod.coerce.date(),
        }),
      ),
    }),
  ),
  budget: zod.object({
    estimatedCents: zod
      .number()
      .describe("Sum of budgetCents across every active wish."),
    actualCents: zod
      .number()
      .describe("Sum of budgetCents across wishes whose purchasedAt is set."),
    totalCount: zod
      .number()
      .describe("Total number of active (non-archived) wishes in the family."),
    purchasedCount: zod.number(),
    wrappedCount: zod.number(),
  }),
});

/**
 * Owner/admin only. Toggles purchased / wrapped state and sets the
per-wish estimated budget (in integer cents). Sending
`purchased: false` or `wrapped: false` clears the corresponding
timestamp; sending `true` stamps it to the current time.
Sending `budgetCents: null` clears the budget.

Cross-tenant access returns 404. Children and players are
rejected with 403.

 * @summary Admin update of a wish's host-only workshop fields
 */
export const UpdateWorkshopWishParams = zod.object({
  id: zod.coerce.number(),
});

export const updateWorkshopWishBodyBudgetCentsMin = 0;
export const updateWorkshopWishBodyBudgetCentsMax = 1000000;

export const UpdateWorkshopWishBody = zod.object({
  purchased: zod
    .boolean()
    .nullish()
    .describe(
      "true → stamp purchasedAt = now, false → clear purchasedAt,\nnull\/omitted → leave unchanged.\n",
    ),
  wrapped: zod
    .boolean()
    .nullish()
    .describe("Same semantics as `purchased` for wrappedAt."),
  budgetCents: zod
    .number()
    .min(updateWorkshopWishBodyBudgetCentsMin)
    .max(updateWorkshopWishBodyBudgetCentsMax)
    .nullish()
    .describe(
      "Estimated spend in integer cents. null clears the value.\nOmit the field entirely to leave it unchanged.\n",
    ),
});

export const UpdateWorkshopWishResponse = zod.object({
  id: zod.number(),
  ownerUserId: zod.number(),
  title: zod.string(),
  description: zod.string().nullish(),
  priority: zod.enum(["low", "normal", "top"]),
  category: zod
    .enum(["toy", "book", "clothing", "experience", "game", "other"])
    .nullish(),
  sortOrder: zod.number(),
  archivedAt: zod.coerce.date().nullish(),
  purchasedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Host-only. Set when an admin marks the wish as purchased via the\nGift Workshop. Always omitted from non-admin endpoints so\nchildren\/players can't infer who is getting what for Christmas.\n",
    ),
  wrappedAt: zod.coerce
    .date()
    .nullish()
    .describe("Host-only. Set when an admin marks the wish as wrapped."),
  budgetCents: zod
    .number()
    .nullish()
    .describe(
      "Host-only. Estimated spend for this wish in integer cents\n(e.g. $24.99 → 2499). Always null on non-admin endpoints.\n",
    ),
  claimedByUserId: zod
    .number()
    .nullish()
    .describe(
      "User id of the family member who has claimed this wish. Null\nwhen unclaimed. Omitted entirely from responses to the wish\nowner so they can't tell whether anyone has claimed it.\n",
    ),
  claimedByDisplayName: zod
    .string()
    .nullish()
    .describe(
      "Display name of the claimer, denormalized for convenience.\nOmitted from responses to the wish owner.\n",
    ),
  claimedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Timestamp the wish was claimed. Omitted from responses to\nthe wish owner.\n",
    ),
  createdAt: zod.coerce.date(),
  updatedAt: zod.coerce.date(),
});

/**
 * Marks a wish on another family member's list as "I'm buying this one"
so two adults don't end up doubling up on the same gift. Only
non-child members of the tenant may claim. The wish's owner can
never see who (or whether anyone) claimed their wish — claim status
is hidden from the owner everywhere in the API to keep it a
surprise. A wish that is already claimed by someone else returns
409. Claiming your own wish or an archived/missing wish returns
404 (existence is never leaked across tenants).

 * @summary Claim a family member's wish (Gift Magic v2)
 */
export const ClaimWishParams = zod.object({
  id: zod.coerce.number(),
});

export const ClaimWishResponse = zod.object({
  id: zod.number(),
  ownerUserId: zod.number(),
  title: zod.string(),
  description: zod.string().nullish(),
  priority: zod.enum(["low", "normal", "top"]),
  category: zod
    .enum(["toy", "book", "clothing", "experience", "game", "other"])
    .nullish(),
  sortOrder: zod.number(),
  archivedAt: zod.coerce.date().nullish(),
  purchasedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Host-only. Set when an admin marks the wish as purchased via the\nGift Workshop. Always omitted from non-admin endpoints so\nchildren\/players can't infer who is getting what for Christmas.\n",
    ),
  wrappedAt: zod.coerce
    .date()
    .nullish()
    .describe("Host-only. Set when an admin marks the wish as wrapped."),
  budgetCents: zod
    .number()
    .nullish()
    .describe(
      "Host-only. Estimated spend for this wish in integer cents\n(e.g. $24.99 → 2499). Always null on non-admin endpoints.\n",
    ),
  claimedByUserId: zod
    .number()
    .nullish()
    .describe(
      "User id of the family member who has claimed this wish. Null\nwhen unclaimed. Omitted entirely from responses to the wish\nowner so they can't tell whether anyone has claimed it.\n",
    ),
  claimedByDisplayName: zod
    .string()
    .nullish()
    .describe(
      "Display name of the claimer, denormalized for convenience.\nOmitted from responses to the wish owner.\n",
    ),
  claimedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Timestamp the wish was claimed. Omitted from responses to\nthe wish owner.\n",
    ),
  createdAt: zod.coerce.date(),
  updatedAt: zod.coerce.date(),
});

/**
 * Clears the claim on a wish that the signed-in user previously
claimed. If plans change and someone else should buy it, the
claimer unclaims and the wish becomes available again.

Only the user who originally made the claim may unclaim — other
family members get 404 (not 403) so the existence of the claim
doesn't leak to non-claimers. The owner of the wish never sees
the claim, so calling unclaim on their own wish always returns
404.

 * @summary Release a claim you previously made on a wish
 */
export const UnclaimWishParams = zod.object({
  id: zod.coerce.number(),
});

export const UnclaimWishResponse = zod.object({
  id: zod.number(),
  ownerUserId: zod.number(),
  title: zod.string(),
  description: zod.string().nullish(),
  priority: zod.enum(["low", "normal", "top"]),
  category: zod
    .enum(["toy", "book", "clothing", "experience", "game", "other"])
    .nullish(),
  sortOrder: zod.number(),
  archivedAt: zod.coerce.date().nullish(),
  purchasedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Host-only. Set when an admin marks the wish as purchased via the\nGift Workshop. Always omitted from non-admin endpoints so\nchildren\/players can't infer who is getting what for Christmas.\n",
    ),
  wrappedAt: zod.coerce
    .date()
    .nullish()
    .describe("Host-only. Set when an admin marks the wish as wrapped."),
  budgetCents: zod
    .number()
    .nullish()
    .describe(
      "Host-only. Estimated spend for this wish in integer cents\n(e.g. $24.99 → 2499). Always null on non-admin endpoints.\n",
    ),
  claimedByUserId: zod
    .number()
    .nullish()
    .describe(
      "User id of the family member who has claimed this wish. Null\nwhen unclaimed. Omitted entirely from responses to the wish\nowner so they can't tell whether anyone has claimed it.\n",
    ),
  claimedByDisplayName: zod
    .string()
    .nullish()
    .describe(
      "Display name of the claimer, denormalized for convenience.\nOmitted from responses to the wish owner.\n",
    ),
  claimedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Timestamp the wish was claimed. Omitted from responses to\nthe wish owner.\n",
    ),
  createdAt: zod.coerce.date(),
  updatedAt: zod.coerce.date(),
});

/**
 * Accepts an ordered array of wish IDs. The server assigns sortOrder
0, 1, 2, … in the given order. Only wishes owned by the current user
in the active tenant are accepted — any ID that does not belong to
the caller is silently skipped (404 is not returned) so the client
does not need to handle partial failures.

 * @summary Update sortOrder for one or more of the signed-in user's wishes
 */
export const reorderWishesBodyOrderedIdsMax = 200;

export const ReorderWishesBody = zod.object({
  orderedIds: zod
    .array(zod.number())
    .min(1)
    .max(reorderWishesBodyOrderedIdsMax),
});

export const ReorderWishesResponseItem = zod.object({
  id: zod.number(),
  ownerUserId: zod.number(),
  title: zod.string(),
  description: zod.string().nullish(),
  priority: zod.enum(["low", "normal", "top"]),
  category: zod
    .enum(["toy", "book", "clothing", "experience", "game", "other"])
    .nullish(),
  sortOrder: zod.number(),
  archivedAt: zod.coerce.date().nullish(),
  purchasedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Host-only. Set when an admin marks the wish as purchased via the\nGift Workshop. Always omitted from non-admin endpoints so\nchildren\/players can't infer who is getting what for Christmas.\n",
    ),
  wrappedAt: zod.coerce
    .date()
    .nullish()
    .describe("Host-only. Set when an admin marks the wish as wrapped."),
  budgetCents: zod
    .number()
    .nullish()
    .describe(
      "Host-only. Estimated spend for this wish in integer cents\n(e.g. $24.99 → 2499). Always null on non-admin endpoints.\n",
    ),
  claimedByUserId: zod
    .number()
    .nullish()
    .describe(
      "User id of the family member who has claimed this wish. Null\nwhen unclaimed. Omitted entirely from responses to the wish\nowner so they can't tell whether anyone has claimed it.\n",
    ),
  claimedByDisplayName: zod
    .string()
    .nullish()
    .describe(
      "Display name of the claimer, denormalized for convenience.\nOmitted from responses to the wish owner.\n",
    ),
  claimedAt: zod.coerce
    .date()
    .nullish()
    .describe(
      "Timestamp the wish was claimed. Omitted from responses to\nthe wish owner.\n",
    ),
  createdAt: zod.coerce.date(),
  updatedAt: zod.coerce.date(),
});
export const ReorderWishesResponse = zod.array(ReorderWishesResponseItem);
